import React, { useMemo, useRef, useState } from 'react'; // importar React y hooks para estado/memo
import { Box, Button, Step, StepLabel, Stepper, Typography, Paper, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Backdrop } from '@material-ui/core'; // importar componentes de UI de Material
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'; // icono para eliminar filas/cortes
import { DatePicker } from '@material-ui/pickers';
import Header from 'src/components/Header'; // importar encabezado estándar con menú hamburguesa
import { useHistory } from 'react-router-dom'; // importar history para navegar entre rutas
import { IonContent } from '@ionic/react'; // contenedor con scroll nativo en móvil
import Autocomplete from '@material-ui/lab/Autocomplete'; // autocompletar de Material
import { useReemplazosApi } from '../hooks/useReemplazosApi'; // hook de API
import { useTimeFormat } from '../hooks/useTimeFormat'; // formato de hora local
import './ReemplazosV2.css'; // estilos personalizados para scrollbars y carrusel

// UI base del asistente de reemplazos 2.0
// Mantiene el mismo backend y contratos, solo mejora UX y robustez

const ReemplazosV2: React.FC = () => {
  const history = useHistory(); // obtener history para navegación programática
  const api = useReemplazosApi(); // api del módulo
  const apiEliminarReemplazo = useReemplazosApi().eliminarReemplazo; // acción puntual
  const { toHHmmLocal } = useTimeFormat(); // util de hora local

  // selector de tipo de reemplazo: DIRECTO, VACACIONES
  const tiposReemplazo = [
    { value: 'DIRECTO', label: 'Reemplazo Directo' }, // valor por defecto
    { value: 'VACACIONES', label: 'Por Rango' }, // preset de motivo editable
  ]; // lista de opciones
  const [tipoReemplazo, setTipoReemplazo] = useState<string>('DIRECTO'); // estado del tipo de reemplazo

  // estado de paso 1 (operario) y 2 (fechas)
  const [operarios, setOperarios] = useState<string[]>([]);
  const [operarioRemplazado, setOperarioRemplazado] = useState<string | null>(null);
  const [operarioRemplazante, setOperarioRemplazante] = useState<string | null>(null);
  const motivos = [
    { value: 'Accidente', label: 'Accidente' },
    { value: 'Enfermedad', label: 'Enfermedad' },
    { value: 'Transporte', label: 'Transporte' },
    { value: 'Asalto', label: 'Asalto' },
    { value: 'Permiso', label: 'Permiso' },
    { value: 'Duelo', label: 'Duelo' },
    { value: 'Renuncia', label: 'Renuncia' },
    { value: 'Vacaciones', label: 'Vacaciones' },
    { value: 'Suspendido', label: 'Suspendido' },
    { value: 'CambioLibre', label: 'Cambio Día Libre' },
    { value: 'noContesta', label: 'No contesta' },
    { value: 'NoAceptaAsignacion', label: 'No acepta asignación' },
    { value: 'consultaMedica', label: 'Consulta Médica' },
    { value: 'reposoMedico', label: 'Reposo Médico' },
    { value: 'eventoFamiliar', label: 'Eventos Familiares' },
    { value: 'gestionesParticulares', label: 'Gestiones Particulares' },
    { value: 'Viajes', label: 'Viajes' },
    { value: 'gestionAutomatica', label: 'Gestión Automática' },
    { value: 'controlMarcacion', label: 'Control de marcación' },
    { value: 'Otros', label: 'Otros' },
  ];
  const [motivo, setMotivo] = useState<string>("");
  const [fechaInicio, setFechaInicio] = useState<string>('');
  const [fechaFin, setFechaFin] = useState<string>('');
  const [errorPaso1, setErrorPaso1] = useState<string>('');
  const [reemplazosExistentes, setReemplazosExistentes] = useState<any[]>([]);
  const [errorListado, setErrorListado] = useState<string>('');
  const [errorPaso2, setErrorPaso2] = useState<string>('');
  const [errorPaso3, setErrorPaso3] = useState<string>(''); // error de punto de servicio
  const [errorPaso4, setErrorPaso4] = useState<string>(''); // error de asignaciones/cupos

  // estado de paso 3 y 4: PS, asignaciones y cupos
  const [asignaciones, setAsignaciones] = useState<any[]>([]); // lista completa
  const [puntosServicio, setPuntosServicio] = useState<string[]>([]); // nombres únicos de PS
  const [puntoSeleccionado, setPuntoSeleccionado] = useState<string>(''); // PS elegido
  const [idsSeleccionados, setIdsSeleccionados] = useState<number[]>([]); // ids de asignaciones elegidas
  const [psIdResuelto, setPsIdResuelto] = useState<number | null>(null); // ps_id detectado
  const [cupoCalculado, setCupoCalculado] = useState<any | null>(null); // resultado de cupos
  // Loading del cálculo de cupos (auto-cálculo). Se usa Backdrop para bloquear taps durante la consulta.
  const [isCalculatingCupos, setIsCalculatingCupos] = useState<boolean>(false);
  // Control de concurrencia: invalida respuestas viejas si se dispara un cálculo nuevo.
  const cupoCalcReqIdRef = useRef<number>(0);
  // paso 5: horario y días
  const [horaEntrada, setHoraEntrada] = useState<string>('');
  const [horaSalida, setHoraSalida] = useState<string>('');
  const [diasDisponibles, setDiasDisponibles] = useState<Array<{ dia: string|null; nombre: string }>>([]);
  const [diasSeleccionados, setDiasSeleccionados] = useState<string[]>([]);
  const [fechasCupo, setFechasCupo] = useState<Array<{ fecha: string; dia: string; cupo_total: string }>>([]);
  // cache simple para evitar recalcular el mismo rango/caso varias veces (cambia entre COBERTURA y AUSENCIA)
  const fechasCupoCacheKeyRef = React.useRef<string>('');
  const [fechasSeleccionadas, setFechasSeleccionadas] = useState<string[]>([]);
  const [openFechasModal, setOpenFechasModal] = useState<boolean>(false);
  const [modalDayFilter, setModalDayFilter] = useState<string | null>(null);
  const [errorPaso5, setErrorPaso5] = useState<string>('');
  // NUEVO: modelo de fechas Ausencia (origen) y Cobertura (destino)
  const [ausenciaInicio, setAusenciaInicio] = useState<string>(''); // fecha inicio de ausencia
  const [ausenciaFin, setAusenciaFin] = useState<string>(''); // fecha fin de ausencia
  const [vacacionesCubrirTodo, setVacacionesCubrirTodo] = useState<boolean>(false); // cubrir todo el rango en Vacaciones (todos los PS)
  // NUEVO (Compensación): estados para manejar reservas y cupo virtual
  const [openCompModal, setOpenCompModal] = useState<boolean>(false); // abrir/cerrar modal
  const [compStep, setCompStep] = useState<number>(1); // 1: origen, 2: destino
  const [saldoOrigen, setSaldoOrigen] = useState<Array<{ fecha: string; dia: string; saldo: string }>>([]); // saldos por fecha
  const [origenFecha, setOrigenFecha] = useState<string>(''); // fecha origen elegida
  const [origenMin, setOrigenMin] = useState<string>('00:00'); // minutos a compensar (se deriva del saldo)
  const [destFechas, setDestFechas] = useState<string[]>([]); // fechas destino elegidas
  const [cupoVirtualPorFecha, setCupoVirtualPorFecha] = useState<Record<string, string>>({}); // cupo virtual acumulado
  const [compensaciones, setCompensaciones] = useState<Array<{ fechaOrigen: string; fechaDestino: string; minutos: string }>>([]); // resumen texto
  const [compError, setCompError] = useState<string>(''); // errores del modal
  // NUEVO: fechas destino específicas para la UI de Compensación (basadas en COBERTURA)
  const [fechasCupoDestinoComp, setFechasCupoDestinoComp] = useState<Array<{ fecha: string; dia: string; cupo_total: string }>>([]);
  // segmentos múltiples de reemplazo (cada uno con su reemplazante y tramo horario)
  const [segmentos, setSegmentos] = useState<Array<{ id: string; operario: string | null; horaEntrada: string; horaSalida: string; dias: string[]; fechas: string[]; error?: string }>>([]);
  const [activeSegIdForDates, setActiveSegIdForDates] = useState<string | null>(null);
  // ids de segmentos expandidos para mostrar los días inline dentro de la card
  const [expandedSegIds, setExpandedSegIds] = useState<string[]>([]); // administrar expansión por card
  const [editHorasSegIds, setEditHorasSegIds] = useState<string[]>([]); // permitir edición manual de horas en "cubrir todo"
  const [tramosPorSeg, setTramosPorSeg] = useState<Record<string, Array<{ ent: string; sal: string }>>>({}); // cortes por card
  // estado de finalización y guardado
  const [preCargas, setPreCargas] = useState<any[]>([]); // lista de items para guardar en lote
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [successText, setSuccessText] = useState<string>('');
  // Resumen de lo efectivamente guardado (se conserva aunque se limpie el formulario/pre-cargas).
  // Esto evita que la UI quede "en blanco" y permite confirmar qué se creó antes de finalizar.
  const [successResumen, setSuccessResumen] = useState<any | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Bloqueo sincrónico para evitar doble tap/doble request.
  // `setIsSaving(true)` puede tardar en reflejarse por el ciclo de render, pero el ref bloquea al instante.
  const savingLockRef = useRef<boolean>(false);
  const [openConfirmModal, setOpenConfirmModal] = useState<boolean>(false); // modal de confirmación final
  const fieldHeight = 36; // alto consistente para inputs/botones en móvil (móvil compacto)

  // efecto: presets y ajustes suaves según tipo de reemplazo
  React.useEffect(() => {
    // Si hay un cálculo en vuelo, lo invalidamos al cambiar de tipo y apagamos el loading.
    cupoCalcReqIdRef.current += 1;
    setIsCalculatingCupos(false);

    // Resetear estado al cambiar de tipo (excepto el tipo mismo)
    setOperarioRemplazado(null);
    setOperarioRemplazante(null);
    setMotivo('');
    setFechaInicio('');
    setFechaFin('');
    setAusenciaInicio('');
    setAusenciaFin('');
    setAsignaciones([]);
    setPuntosServicio([]);
    setPuntoSeleccionado('');
    setIdsSeleccionados([]);
    setCupoCalculado(null);
    setHoraEntrada('');
    setHoraSalida('');
    setDiasDisponibles([]);
    setDiasSeleccionados([]);
    setSegmentos([]);
    setPreCargas([]);
    setReemplazosExistentes([]);
    setErrorListado('');
    setErrorPaso1('');
    setErrorPaso2('');
    setErrorPaso3('');
    setErrorPaso4('');
    setErrorPaso5('');
    setShowSuccess(false);
    setSuccessText('');
    // limpiar compensación
    setOpenCompModal(false);
    setCompStep(1);
    setSaldoOrigen([]);
    setOrigenFecha('');
    setOrigenMin('00:00');
    setDestFechas([]);
    setCupoVirtualPorFecha({});
    setCompensaciones([]);
    setCompError('');

    // si el tipo es VACACIONES y no hay motivo (o viene uno de permisos), presetear "Vacaciones"
    if (tipoReemplazo === 'VACACIONES') { // verificar tipo seleccionado
      if (!motivo || motivo === 'Permiso') setMotivo('Vacaciones'); // establecer motivo por defecto editable
    } // fin VACACIONES
    // en DIRECTO no tocamos el motivo automáticamente
  }, [tipoReemplazo]); // dependencias: tipo de reemplazo

  // util: obtener tramos horarios únicos de una asignación (independiente del día)
  const getTramosAsignacion = (a: any): string[] => {
    try {
      const dias = [
        ['lun_ent', 'lun_sal'],
        ['mar_ent', 'mar_sal'],
        ['mie_ent', 'mie_sal'],
        ['jue_ent', 'jue_sal'],
        ['vie_ent', 'vie_sal'],
        ['sab_ent', 'sab_sal'],
        ['dom_ent', 'dom_sal'],
      ];
      const set = new Set<string>();
      dias.forEach(([e, s]) => {
        const he = a?.[e];
        const hs = a?.[s];
        if (he && hs) set.add(`${he}-${hs}`);
      });
      return Array.from(set);
    } catch {
      return [];
    }
  };

  // util: HH:mm -> minutos
  const hhmmToMin = (hhmm: string): number => {
    try {
      const [h, m] = (hhmm || '0:00').split(':').map((n) => parseInt(String(n), 10) || 0);
      return (h * 60) + m;
    } catch {
      return 0;
    }
  };

  // util: minutos -> HH:mm
  const minToHHmm = (min: number): string => {
    const safe = Math.max(0, Math.floor(min || 0));
    const hh = String(Math.floor(safe / 60)).padStart(2, '0');
    const mm = String(safe % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // util: obtener tramos horarios desde un registro de vista_remplazo (r)
  const getTramosDeReemplazo = (r: any): string[] => {
    try {
      const dias = [
        ['lun_ent', 'lun_sal'],
        ['mar_ent', 'mar_sal'],
        ['mie_ent', 'mie_sal'],
        ['jue_ent', 'jue_sal'],
        ['vie_ent', 'vie_sal'],
        ['sab_ent', 'sab_sal'],
        ['dom_ent', 'dom_sal'],
      ];
      const set = new Set<string>();
      dias.forEach(([e, s]) => {
        const he = r?.[e];
        const hs = r?.[s];
        if (he && hs) set.add(`${he}-${hs}`);
      });
      return Array.from(set);
    } catch { return []; }
  };

  // intentar resolver ps_id desde las asignaciones seleccionadas (para reforzar el descuento por PS)
  const resolvePsId = React.useCallback((): number | null => {
    try {
      const keys = ['punto_servicio_id','PuntoServicio_id','ps_id','puntoServicio_id','PuntoServicioId','PuntoServicioID'];
      const cand = asignaciones
        .filter((a: any) => idsSeleccionados.includes(a?.id))
        .sort((a: any, b: any) => (a?.NombrePServicio === puntoSeleccionado ? -1 : 1));
      for (const a of cand) {
        for (const k of keys) {
          const v = a?.[k];
          if (v !== undefined && v !== null && String(v) !== '') {
            const n = parseInt(String(v), 10);
            if (!Number.isNaN(n) && n > 0) return n;
          }
        }
      }
    } catch {}
    return null;
  }, [asignaciones, idsSeleccionados, puntoSeleccionado]);

  const handleCancel = () => {
    // cancelar el flujo: reiniciar estado y volver a inicio
    resetAll();
    history.push('/home');
  };

  // reiniciar todo el estado del flujo (sin navegar)
  const resetAll = () => {
    setOperarioRemplazado(null);
    setOperarioRemplazante(null);
    setMotivo('');
    setFechaInicio('');
    setFechaFin('');
    setAsignaciones([]);
    setPuntosServicio([]);
    setPuntoSeleccionado('');
    setIdsSeleccionados([]);
    setCupoCalculado(null);
    setHoraEntrada('');
    setHoraSalida('');
    setDiasDisponibles([]);
    setDiasSeleccionados([]);
    setSegmentos([]);
    setPreCargas([]);
    setReemplazosExistentes([]);
    setErrorListado('');
    setErrorPaso1('');
    setErrorPaso2('');
    setErrorPaso3('');
    setErrorPaso4('');
    setErrorPaso5('');
    setShowSuccess(false);
    setSuccessText('');
    setSuccessResumen(null);
    // limpiar compensación
    setOpenCompModal(false);
    setCompStep(1);
    setSaldoOrigen([]);
    setOrigenFecha('');
    setOrigenMin('00:00');
    setDestFechas([]);
    setCupoVirtualPorFecha({});
    setCompensaciones([]);
    setCompError('');
  };

  // guardar reemplazo y mostrar pantalla de éxito
  // helper: construir items a partir del formulario actual (expande segmentos)
  const construirItemsActuales = (): any[] => {
    // construir arreglo de payloads: usar segmentos si existen; si no, usar el único
    const fechaAus = (ausenciaInicio || fechaInicio);
    const baseItem = {
      remplazado: operarioRemplazado, // operario a reemplazar
      fechaAusencia: (tipoReemplazo === 'DIRECTO' ? fechaAus : null), // directo: un solo dia
      fechaIniAusencia: (tipoReemplazo === 'VACACIONES' ? (ausenciaInicio || fechaInicio) : fechaAus),
      fechaFinAusencia: (tipoReemplazo === 'VACACIONES' ? (ausenciaFin || ausenciaInicio || fechaInicio) : fechaAus),
      tipoReemplazoV2: (tipoReemplazo === 'VACACIONES' ? 'Vacaciones' : 'Directo'),
      fechaInicioRemplazante: fechaInicio, // rango desde
      fechaFinRemplazante: fechaFin, // rango hasta
      puntoServicio: puntoSeleccionado, // PS seleccionado (puede variar si se cubre todo)
      asignaciones_id: idsSeleccionados, // ids de asignaciones (puede variar si se cubre todo)
      motivo, // motivo actual (editable siempre)
    };

    // Reglas de negocio (confirmadas): en VACACIONES (Por Rango) no se debe generar reemplazo para múltiples
    // puntos de servicio; solo aplica al PS seleccionado por el usuario.
    const porPS: Array<{ ps: string; ids: number[] }> = [{ ps: puntoSeleccionado, ids: idsSeleccionados }];

    // Generar items a partir de segmentos × PS
    const expandir = (seg: { id?: string; operario: any; horaEntrada: string; horaSalida: string; dias: string[]; fechas: string[] }) => {

      // Caso general: soportar múltiples cortes por card usando tramosPorSeg; si no hay, usar horaEntrada/horaSalida del segmento
      const fechasDelSeg = (() => {
        if (tipoReemplazo === 'DIRECTO' && fechaInicio) {
          return [fechaInicio]; // un solo día de cobertura
        }
        return seg.fechas?.length ? seg.fechas : getDatesForSelectedDays(fechaInicio, fechaFin, seg.dias || []);
      })(); // fechas aplicables
      const segId = String(seg.id || ''); // id único de la card
      const customTramos = tramosPorSeg[segId]; // cortes configurados por el usuario
      const tramos = (customTramos && customTramos.length)
        ? customTramos // usar cortes definidos
        : [{ ent: seg.horaEntrada, sal: seg.horaSalida }]; // fallback a los valores simples del segmento
      const tramosValidos = (tramos || []).filter((t) => t && t.ent && t.sal);

      // DIRECTO (AUSENCIA != COBERTURA):
      // - Guardamos en la fecha de COBERTURA (fechaInicio)
      // - Pero consumimos el cupo del día de AUSENCIA (ausenciaInicio) vía COMPENSACION
      //   con minutos equivalentes a cada tramo.
      const autoCompensar = (
        tipoReemplazo === 'DIRECTO'
        && !!ausenciaInicio
        && !!fechaInicio
        && ausenciaInicio !== fechaInicio
        && (compensaciones || []).length === 0
      );

      return porPS.flatMap(({ ps, ids }) =>
        tramosValidos.map(({ ent, sal }) => {
          const durMin = Math.max(0, hhmmToMin(sal) - hhmmToMin(ent));
          const compAuto = autoCompensar
            ? [{ fechaOrigen: ausenciaInicio, fechaDestino: fechaInicio, minutos: minToHHmm(durMin) }]
            : [];

          return ({
            ...baseItem, // campos comunes
            puntoServicio: ps, // PS destino
            asignaciones_id: ids, // asignaciones aplicadas
            remplazante: seg.operario, // operario reemplazante
            horaEntradaRemplazante: ent || '', // hora de entrada del corte
            horaSalidaRemplazante: sal || '', // hora de salida del corte
            dias: seg.dias || [], // días por segmento
            fechas: fechasDelSeg, // fechas calculadas por días o seleccionadas
            ...(
              (compensaciones.length || (autoCompensar && compAuto.length))
                ? { modo: 'COMPENSACION', compensaciones: (compensaciones.length ? compensaciones : compAuto) }
                : {}
            ), // metadata de compensación (manual o automática)
          });
        })
      );
    };

  const arr = (segmentos.length > 0
      ? segmentos.flatMap((s) => expandir({ id: s.id, operario: s.operario, horaEntrada: s.horaEntrada, horaSalida: s.horaSalida, dias: s.dias || [], fechas: s.fechas || [] }))
      : porPS.map(({ ps, ids }) => ({
          ...baseItem,
          puntoServicio: ps,
          asignaciones_id: ids,
          remplazante: operarioRemplazante,
          horaEntradaRemplazante: horaEntrada,
          horaSalidaRemplazante: horaSalida,
          dias: diasSeleccionados,
          fechas: fechasSeleccionadas?.length ? fechasSeleccionadas : getDatesForSelectedDays(fechaInicio, fechaFin, diasSeleccionados || []),
          ...((tipoReemplazo === 'COMPENSACION' && compensaciones.length) ? { modo: 'COMPENSACION', compensaciones } : {}),
        }))
    );
    return arr; // devolver arreglo de items
  };

  // acción: agregar el formulario actual a la lista de pre-cargas
  const agregarAPreCarga = async () => {
    const v = await validarAntesDeContinuar(); // validar reglas por tipo y comunes
    if (!v.ok) { setErrorPaso5(v.error || 'No se puede agregar.'); return; } // bloquear si falla
    const items = construirItemsActuales(); // expandir segmentos en items compatibles
    setPreCargas((prev) => ([ ...prev, ...items ])); // acumular en la lista
    // limpiar sólo los elementos de construcción de segmento para permitir armar la siguiente
    setSegmentos([{ id: String(Date.now()), operario: null, horaEntrada: '', horaSalida: '', dias: [], fechas: [] }]); // nueva card vacía
    setDiasSeleccionados([]); // limpiar selección global de días
    setFechasSeleccionadas([]); // limpiar fechas seleccionadas globales
    // limpiar compensación (siempre) para evitar mezclas entre pre-cargas
    setOpenCompModal(false); setCompStep(1); setSaldoOrigen([]); setOrigenFecha(''); setOrigenMin('00:00'); setDestFechas([]); setCupoVirtualPorFecha({}); setCompensaciones([]); setCompError('');
  };

  // acción: guardar todas las pre-cargas en un único request
  const guardarPreCargas = async () => {
    // Evitar doble tap: bloqueo inmediato (sin esperar re-render)
    if (savingLockRef.current || isSaving || preCargas.length === 0) return; // no hacer nada si está guardando o lista vacía
    try {
      savingLockRef.current = true; // tomar lock
      setIsSaving(true); // marcar guardado en progreso

      // VACACIONES: aplicar auto-compensación también cuando se guarda un lote de pre-cargas.
      let payloadLote = preCargas;
      const comp = await aplicarAutoCompensacionVacaciones(payloadLote);
      if (!comp.ok) { alert(comp.error || 'No se pudo auto-compensar el lote.'); return; }
      payloadLote = comp.payload;

      const res = await api.crearRemplazo(payloadLote); // enviar lote
      if (res.ok) {
        setSuccessText(res.data || `Se guardaron ${preCargas.length} reemplazos.`); // mensaje de éxito
        // Guardar un resumen para mostrar en pantalla de éxito.
        setSuccessResumen({
          tipoReemplazo,
          ausenciaInicio,
          ausenciaFin,
          coberturaInicio: fechaInicio,
          coberturaFin: fechaFin,
          puntoServicio: puntoSeleccionado,
          remplazado: operarioRemplazado,
          items: preCargas,
        });
        setShowSuccess(true); // mostrar pantalla de éxito
        // limpiar estado de construcción y lista
        setPreCargas([]); // vaciar pre-cargas
      } else {
        const diag = await api.diagnosticoRemplazo({
          items: payloadLote,
          fecha_ausencia: (ausenciaInicio || fechaInicio),
          ps_id: resolvePsId() || psIdResuelto,
          puntoServicio: puntoSeleccionado,
          asignaciones_id: idsSeleccionados,
          remplazado: operarioRemplazado,
        });
        const razones = diag.ok ? (diag.data?.reasons || []) : [];
        const detalle = razones.length
          ? `No se puede por esto:\n${razones.map((r: any) => `- ${r.message}`).join('\n')}`
          : (res.error || 'Error al crear reemplazos del lote');
        alert(detalle); // error controlado
      }
    } finally {
      setIsSaving(false); // finalizar guardado
      savingLockRef.current = false; // liberar lock
    }
  };

  const finalizar = async (opts?: { skipPreCheck?: boolean }) => {
    if (isSaving) return;
    // Directo: si el día destino no tiene asignación, sugerir compensación antes de validar/guardar
    if (!opts?.skipPreCheck && preCheckDirectoCompensacion()) return;

    // Validar antes de abrir modal
    const v = await validarAntesDeContinuar();
    if (!v.ok) { setErrorPaso5(v.error || 'Error de validación.'); return; }

    // Abrir modal de confirmación
    setOpenConfirmModal(true);
  };

  // VACACIONES (Por Rango): auto-compensar cupo de AUSENCIA hacia fechas destino.
  // Objetivo de negocio:
  // - Permitir cargar horas en una fecha destino específica aunque su cupo diario propio sea bajo,
  //   usando el total disponible del rango AUSENCIA (consumiendo saldos reales por día).
  // Implementación:
  // - Calcula minutos solicitados por fecha destino (sumando todos los ítems/tramos del payload).
  // - Compara contra cupo diario remanente de esa fecha (endpoint cupoPorDia).
  // - Si falta cupo, distribuye minutos faltantes tomando saldo por día dentro del rango AUSENCIA
  //   (endpoint compensacionSaldoOrigen) y arma `compensaciones`.
  // - Para no duplicar minutos en backend, adjunta TODAS las compensaciones a un solo ítem del lote.
  const aplicarAutoCompensacionVacaciones = async (
    payloadItems: any[]
  ): Promise<{ ok: boolean; payload: any[]; error?: string }> => {
    try {
      if (tipoReemplazo !== 'VACACIONES') return { ok: true, payload: payloadItems };
      if (!ausenciaInicio || !ausenciaFin) return { ok: true, payload: payloadItems };
      if (!payloadItems || payloadItems.length === 0) return { ok: true, payload: payloadItems };

      // Si el usuario ya armó una compensación manual/previa, no auto-inyectar otra.
      const yaTraeComp = payloadItems.some((it: any) => (
        it?.modo === 'COMPENSACION'
        && Array.isArray(it?.compensaciones)
        && it.compensaciones.length > 0
      ));
      if (yaTraeComp) return { ok: true, payload: payloadItems };

      // Validación defensiva: este flujo asume un único PS/asignaciones en el request.
      const keyOf = (it: any) => {
        const ps = String(it?.puntoServicio || '');
        const ids = Array.isArray(it?.asignaciones_id) ? it.asignaciones_id.join(',') : String(it?.asignaciones_id || '');
        return `${ps}|${ids}`;
      };
      const k0 = keyOf(payloadItems[0]);
      const mixed = payloadItems.some((it: any) => keyOf(it) !== k0);
      if (mixed) {
        return {
          ok: false,
          payload: payloadItems,
          error: 'Para auto-compensar en Por Rango, guardá por separado (no mezcles distintos Puntos de Servicio/asignaciones en el mismo guardado).',
        };
      }

      // 1) Minutos solicitados por fecha destino.
      const reqPorFecha: Record<string, number> = {};
      for (const it of payloadItems) {
        const ent = it?.horaEntradaRemplazante;
        const sal = it?.horaSalidaRemplazante;
        const durMin = Math.max(0, hhmmToMin(sal) - hhmmToMin(ent));
        const fechas = Array.isArray(it?.fechas) ? it.fechas : (it?.fechas ? [it.fechas] : []);
        for (const f of fechas) {
          if (!f) continue;
          reqPorFecha[f] = (reqPorFecha[f] || 0) + durMin;
        }
      }
      const destFechasOrdenadas = Object.keys(reqPorFecha).sort();
      if (destFechasOrdenadas.length === 0) return { ok: true, payload: payloadItems };

      const baseIt = payloadItems[0] || {};
      const psNombre = String(baseIt?.puntoServicio || puntoSeleccionado || '');
      const asignIds = Array.isArray(baseIt?.asignaciones_id) ? baseIt.asignaciones_id : idsSeleccionados;
      const psid = resolvePsId();
      const destIni = destFechasOrdenadas[0];
      const destFin = destFechasOrdenadas[destFechasOrdenadas.length - 1];

      // 2) Cupo diario remanente por fecha destino (sin compensación).
      const cpdDest = await api.cupoPorDia({
        asignaciones_id: asignIds,
        fecha_inicio: destIni,
        fecha_fin: destFin,
        ps_nombre: psNombre,
        remplazado: operarioRemplazado,
        // Igual que al guardar: descontar también lo que el ausente ya trabajó (horas procesadas)
        modo_cupo: 'real_hp',
        ...(psIdResuelto ? { ps_id: psIdResuelto } : {}),
        ...(psid ? { ps_id: psid } : {}),
      });
      if (!cpdDest.ok) {
        return { ok: false, payload: payloadItems, error: cpdDest.error || 'No se pudo obtener el cupo diario para auto-compensar.' };
      }

      const remDestPorFecha: Record<string, number> = {};
      (cpdDest.data || []).forEach((d: any) => { remDestPorFecha[d.fecha] = hhmmToMin(d.cupo_total || '00:00'); });

      // 3) Detectar faltantes por fecha destino.
      const faltantePorFecha: Record<string, number> = {};
      let hayFaltante = false;
      destFechasOrdenadas.forEach((f) => {
        const req = reqPorFecha[f] || 0;
        const rem = remDestPorFecha[f] ?? 0;
        const falta = Math.max(0, req - rem);
        if (falta > 0) hayFaltante = true;
        faltantePorFecha[f] = falta;
      });
      if (!hayFaltante) return { ok: true, payload: payloadItems };

      // 4) Saldos por día dentro del rango AUSENCIA (origen real de horas).
      const saldoOrigenRes = await (api as any).compensacionSaldoOrigen?.({
        asignaciones_id: asignIds,
        fecha_inicio: ausenciaInicio,
        fecha_fin: ausenciaFin,
        ps_nombre: psNombre,
        remplazado: operarioRemplazado,
        ...(psIdResuelto ? { ps_id: psIdResuelto } : {}),
        ...(psid ? { ps_id: psid } : {}),
      });
      if (!saldoOrigenRes?.ok) {
        return { ok: false, payload: payloadItems, error: saldoOrigenRes?.error || 'No se pudo obtener el saldo de AUSENCIA para auto-compensar.' };
      }

      const origenDisponibles: Record<string, number> = {};
      (saldoOrigenRes.data || []).forEach((d: any) => { origenDisponibles[d.fecha] = hhmmToMin(d.saldo || '00:00'); });
      const origenFechasOrdenadas = Object.keys(origenDisponibles).sort();

      // 5) Asignación greedy: llenar faltantes de destino usando saldos de origen.
      const compAuto: Array<{ fechaOrigen: string; fechaDestino: string; minutos: string }> = [];
      for (const fDestino of destFechasOrdenadas) {
        let falta = faltantePorFecha[fDestino] || 0;
        if (falta <= 0) continue;

        for (const fOrigen of origenFechasOrdenadas) {
          if (falta <= 0) break;
          if (fOrigen === fDestino) continue; // evitar compensación origen=destino
          const disp = origenDisponibles[fOrigen] || 0;
          if (disp <= 0) continue;
          const tomar = Math.min(falta, disp);
          compAuto.push({ fechaOrigen: fOrigen, fechaDestino: fDestino, minutos: minToHHmm(tomar) });
          origenDisponibles[fOrigen] = Math.max(0, disp - tomar);
          falta = Math.max(0, falta - tomar);
        }

        if (falta > 0) {
          return {
            ok: false,
            payload: payloadItems,
            error: `No hay saldo suficiente en el rango de AUSENCIA para compensar el día ${fDestino}. Falta ${minToHHmm(falta)}.`,
          };
        }
      }

      // 6) Adjuntar compensaciones a un único ítem para evitar duplicación en backend.
      const payloadOut = payloadItems.map((it: any, idx: number) => {
        if (idx !== 0) return it;
        return { ...it, modo: 'COMPENSACION', compensaciones: compAuto };
      });
      return { ok: true, payload: payloadOut };
    } catch {
      return { ok: false, payload: payloadItems, error: 'Error inesperado al auto-compensar cupos en Por Rango.' };
    }
  };

  const confirmarGuardado = async () => {
    // Evitar doble tap: bloqueo inmediato (sin esperar re-render)
    if (savingLockRef.current || isSaving) return;
    savingLockRef.current = true; // tomar lock
    setOpenConfirmModal(false);
    try {
      setIsSaving(true);
      let payload = construirItemsActuales().filter((it) => it.horaEntradaRemplazante && it.horaSalidaRemplazante); // solo ítems válidos
      if (payload.length === 0) {
        // Fallback automático: si no hay horas en la card, derivar de cortes de asignación.
        // Regla requerida: preferir AUSENCIA (origen) para elegir el corte.
        try {
          const f = (ausenciaInicio || fechaInicio);
          const ps = puntoSeleccionado;
          if (f && ps) {
            const autos = (asignaciones || [])
              .filter((a: any) => (a?.NombrePServicio || '') === ps)
              .map((a: any) => getAssignmentTimesForDate(a, f))
              .filter((x: any) => x?.ent && x?.sal) as Array<{ ent: string; sal: string }>;
            if (autos.length) {
              // setear en todas las cards como cortes por defecto y reintentar
              setTramosPorSeg((prev) => {
                const next = { ...prev };
                (segmentos || []).forEach((sg) => { next[sg.id] = autos.map((x) => ({ ...x })); });
                return next;
              });
              const retry = construirItemsActuales().filter((it) => it.horaEntradaRemplazante && it.horaSalidaRemplazante);
              if (retry.length) payload = retry;
            }
          }
        } catch {}
      }
      if (payload.length === 0) { setErrorPaso5('Completá al menos un horario válido (HH:mm).'); return; }

      // VACACIONES: si el cupo diario del día destino es insuficiente, auto-compensar usando el rango AUSENCIA.
      // Esto evita el error backend "Excede el cupo del YYYY-MM-DD (máx HH:MM)" cuando el total del rango sí alcanza.
      const comp = await aplicarAutoCompensacionVacaciones(payload);
      if (!comp.ok) { alert(comp.error || 'No se pudo auto-compensar.'); return; }
      payload = comp.payload;

      const res = await api.crearRemplazo(payload);
      if (res.ok) {
        setSuccessText(res.data || 'Reemplazo guardado correctamente.');
        // Guardar un resumen para mostrar en pantalla de éxito.
        setSuccessResumen({
          tipoReemplazo,
          ausenciaInicio,
          ausenciaFin,
          coberturaInicio: fechaInicio,
          coberturaFin: fechaFin,
          puntoServicio: puntoSeleccionado,
          remplazado: operarioRemplazado,
          items: payload,
        });
        setShowSuccess(true);
      } else {
        const diag = await api.diagnosticoRemplazo({
          items: payload,
          fecha_ausencia: (ausenciaInicio || fechaInicio),
          ps_id: resolvePsId() || psIdResuelto,
          puntoServicio: puntoSeleccionado,
          asignaciones_id: idsSeleccionados,
          remplazado: operarioRemplazado,
        });
        const razones = diag.ok ? (diag.data?.reasons || []) : [];
        const detalle = razones.length
          ? `No se puede por esto:\n${razones.map((r: any) => `- ${r.message}`).join('\n')}`
          : (res.error || 'Error al crear reemplazo');
        alert(detalle);
      }
    } finally {
      setIsSaving(false);
      savingLockRef.current = false; // liberar lock
    }
  };

  // Guardar forzando compensación para Directo (usa AUSENCIA -> COBERTURA con minutos calculados)
  const guardarConCompensacion = async (minHHMM: string) => {
    // Evitar doble tap: bloqueo inmediato (sin esperar re-render)
    if (savingLockRef.current || isSaving) return;
    try {
      savingLockRef.current = true; // tomar lock
      setIsSaving(true);
      const base = construirItemsActuales();
      // Fallback: si algún item no trae horas, asignar una franja por defecto basada en los minutos a compensar
      const addMin = (hhmm: string, add: string) => {
        try {
          const [h,m]=(hhmm||'00:00').split(':').map(Number);
          const [ah,am]=(add||'00:00').split(':').map(Number);
          const t=(h*60+m)+(ah*60+am);
          const hh=String(Math.floor(t/60)%24).padStart(2,'0');
          const mm=String(t%60).padStart(2,'0');
          return `${hh}:${mm}`;
        } catch { return hhmm || '00:00'; }
      };
      const ensureHoras = (it: any): any => {
        if (it.horaEntradaRemplazante && it.horaSalidaRemplazante) return it;
        const ent = '07:00';
        const sal = addMin(ent, minHHMM || '00:00');
        return { ...it, horaEntradaRemplazante: ent, horaSalidaRemplazante: sal };
      };
      const baseConHoras = base.map(ensureHoras).filter((it: any) => it.horaEntradaRemplazante && it.horaSalidaRemplazante);
      const comp = [{ fechaOrigen: ausenciaInicio, fechaDestino: fechaInicio, minutos: minHHMM }];
      const payload = baseConHoras.map((it: any) => ({ ...it, modo: 'COMPENSACION', compensaciones: comp }));
      const res = await api.crearRemplazo(payload);
      if (res.ok) {
        setSuccessText(res.data || 'Reemplazo guardado correctamente.');
        // Guardar un resumen para mostrar en pantalla de éxito.
        setSuccessResumen({
          tipoReemplazo,
          ausenciaInicio,
          ausenciaFin,
          coberturaInicio: fechaInicio,
          coberturaFin: fechaFin,
          puntoServicio: puntoSeleccionado,
          remplazado: operarioRemplazado,
          items: payload,
        });
        setShowSuccess(true);
      } else {
        const diag = await api.diagnosticoRemplazo({
          items: payload,
          fecha_ausencia: (ausenciaInicio || fechaInicio),
          ps_id: resolvePsId() || psIdResuelto,
          puntoServicio: puntoSeleccionado,
          asignaciones_id: idsSeleccionados,
          remplazado: operarioRemplazado,
        });
        const razones = diag.ok ? (diag.data?.reasons || []) : [];
        const detalle = razones.length
          ? `No se puede por esto:\n${razones.map((r: any) => `- ${r.message}`).join('\n')}`
          : (res.error || 'Error al crear reemplazo');
        alert(detalle);
      }
    } finally {
      setIsSaving(false);
      savingLockRef.current = false; // liberar lock
    }
  };

  // cargar asignaciones cuando cambian Operario o Fechas
  // siempre usar AUSENCIA para determinar PS y cupos (la cobertura puede ser otro día)
  React.useEffect(() => {
    const cargar = async () => {
      const rangoIni = (ausenciaInicio || fechaInicio); // preferir fecha de ausencia para listar asignaciones del operario reemplazado
      const rangoFin = (ausenciaFin || fechaFin); // preferir fecha de ausencia para listar asignaciones del operario reemplazado
      if (operarioRemplazado && rangoIni && rangoFin) {
        const res = await api.listarAsignaciones({
          remplazado: operarioRemplazado,
          fechaInicio: rangoIni,
          fechaFin: rangoFin,
          // TD-11585: si el ausente estaba cubriendo a otro, el backend suma ese cupo como asignación heredada (id negativo)
          incluir_herencia: true,
        });
        if (res.ok) {
          const lista = res.data || [];
          setAsignaciones(lista);
          const ps = Array.from(new Set(lista.map((i: any) => i?.NombrePServicio).filter(Boolean)));
          setPuntosServicio(ps);
          // Importante: NO auto-seleccionar asignaciones en VACACIONES.
          // Se respeta la selección explícita del usuario para el PS elegido.
        } else {
          setAsignaciones([]);
          setPuntosServicio([]);
        }
        // No limpiar puntoSeleccionado/ids/cupos: se respeta lo elegido al navegar
      }
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operarioRemplazado, fechaInicio, fechaFin, ausenciaInicio, ausenciaFin, tipoReemplazo, vacacionesCubrirTodo]);

  // Guardas de UX: evitar cálculos "pegados" si cambia el PS o si cambian las asignaciones cargadas.
  // Si el usuario cambia de Punto de Servicio o el backend devuelve otra lista, filtramos selecciones inválidas.
  React.useEffect(() => {
    if (!idsSeleccionados.length) return;
    try {
      const validIds = new Set(
        (asignaciones || [])
          .filter((a: any) => (a?.NombrePServicio || '') === (puntoSeleccionado || ''))
          .map((a: any) => a?.id)
          .filter((x: any) => x !== undefined && x !== null)
      );
      const filtered = (idsSeleccionados || []).filter((id) => validIds.has(id));
      if (filtered.length !== idsSeleccionados.length) {
        setIdsSeleccionados(filtered);
        setCupoCalculado(null);
        setDetalleCupoDias([]);
        setErrorPaso4('');
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asignaciones, puntoSeleccionado]);

  // bloqueo de navegación para confirmar salida del módulo
  React.useEffect(() => {
    const unblock = history.block((tx: any) => {
      const nextPath = `${tx.pathname || ''}${tx.search || ''}${tx.hash || ''}`;
      // Permitir navegación interna dentro del módulo
      if (nextPath.startsWith('/reemplazos-v2')) return undefined;
      const ok = window.confirm('¿Seguro que quiere salir del módulo Reemplazos? Se perderán los cambios no guardados.');
      if (ok) {
        unblock();
        handleCancel();
        history.push(nextPath);
      }
      return false; // bloquear transición por defecto
    });
    return () => { try { unblock(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refs para contenedores de listas
  // referencias de contenedores (solo segmentos usa scroll horizontal)
  const segListRef = useRef<HTMLDivElement | null>(null); // caja de scroll en Paso 5 (segmentos)

  // helper para configurar el contenedor de segmentos como carrusel horizontal (Paso 5)
  const adjustSegListScroll = React.useCallback(() => {
    const el = segListRef.current; // obtener referencia del contenedor
    if (!el) return; // abortar si no existe
    el.style.maxHeight = 'none'; // no limitar por altura
    el.style.overflowX = 'auto'; // permitir scroll horizontal
    el.style.overflowY = 'hidden'; // evitar scroll vertical
    el.style.overscrollBehavior = 'contain'; // suavizar rebotes
    try { el.style.setProperty('-webkit-overflow-scrolling', 'touch'); } catch {} // inercia en iOS
  }, []);

  // recalcular contenedor scrollable de segmentos (espacio amarillo del mockup)
  React.useEffect(() => {
    const id = window.setTimeout(adjustSegListScroll, 0);
    window.addEventListener('resize', adjustSegListScroll);
    return () => { window.clearTimeout(id); window.removeEventListener('resize', adjustSegListScroll); };
  }, [segmentos.length, adjustSegListScroll]);

  // si no hay segmentos, crear uno vacío (card inicial)
  React.useEffect(() => {
    if (segmentos.length === 0) {
      setSegmentos([{ id: String(Date.now()), operario: null, horaEntrada: '', horaSalida: '', dias: [], fechas: [] }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TD-11585: el listado de existentes cubre AUSENCIA y COBERTURA juntas (la hora se usa del día de
  // ausencia aunque se cubra otro día) y todos los PS del ausente, incluidos los V1 cargados en EMSYS.
  const payloadListadoExistentes = (): any | null => {
    const fechas = [ausenciaInicio, ausenciaFin, fechaInicio, fechaFin].filter(Boolean).sort();
    if (!operarioRemplazado || !fechas.length) return null;
    return { remplazado: operarioRemplazado, fechaInicio: fechas[0], fechaFin: fechas[fechas.length - 1], incluir_v1: true };
  };

  // Auto-cargar reemplazos existentes cuando filtros clave están completos
  React.useEffect(() => {
    const cargar = async () => {
      const payload = payloadListadoExistentes();
      if (!payload) {
        setReemplazosExistentes([]);
        return;
      }
      const res = await api.listarReemplazosExistentes(payload);
      if (res.ok) {
        setReemplazosExistentes(res.data || []);
        try {
          const first = (res.data || []).find((x: any) => x?.ps_id && x?.nombre_ps === puntoSeleccionado);
          if (first && first.ps_id) {
            const n = parseInt(String(first.ps_id), 10);
            if (!Number.isNaN(n) && n > 0) setPsIdResuelto(n);
          }
        } catch {}
        setErrorListado('');
      } else {
        setReemplazosExistentes([]);
        setErrorListado(res.error || 'Error al listar reemplazos');
      }
    };
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoReemplazo, operarioRemplazado, ausenciaInicio, ausenciaFin, fechaInicio, fechaFin, puntoSeleccionado]);

  // Inicializar cortes automáticos por segment/PS cuando se cubre todo el rango en Vacaciones
  React.useEffect(() => {
    if (!(tipoReemplazo === 'VACACIONES' && vacacionesCubrirTodo && puntoSeleccionado)) return;
    const autos = getAutoTramosParaPS(puntoSeleccionado).map((t) => {
      const [ent, sal] = String(t).split('-');
      return { ent: (ent || '').trim(), sal: (sal || '').trim() };
    });
    if (!autos.length) return;
    setTramosPorSeg((prev) => {
      const next: Record<string, Array<{ ent: string; sal: string }>> = { ...prev };
      (segmentos || []).forEach((sg) => {
        if (!next[sg.id] || !next[sg.id].length) {
          next[sg.id] = autos.map((x) => ({ ...x }));
        }
      });
      return next;
    });
  }, [tipoReemplazo, vacacionesCubrirTodo, puntoSeleccionado, ausenciaInicio, ausenciaFin, fechaInicio, fechaFin, asignaciones, segmentos.length]);

  // Directo: siempre un solo día de ausencia y un solo día de cobertura (sin rangos)
  React.useEffect(() => {
    if (tipoReemplazo === 'DIRECTO') {
      if (ausenciaInicio && ausenciaFin !== ausenciaInicio) setAusenciaFin(ausenciaInicio);
      if (fechaInicio && fechaFin !== fechaInicio) setFechaFin(fechaInicio);
    }
  }, [tipoReemplazo, ausenciaInicio, ausenciaFin, fechaInicio, fechaFin]);
  // Regla de negocio: en VACACIONES (Por Rango) el reemplazo se crea solo para el PS seleccionado.
  // Por lo tanto, se deshabilita el comportamiento previo de "Cubrir todo" (que expandía a todos los PS).
  React.useEffect(() => {
    if (tipoReemplazo === 'VACACIONES' && vacacionesCubrirTodo) {
      setVacacionesCubrirTodo(false);
    }
  }, [tipoReemplazo, vacacionesCubrirTodo]);

  // Total de horas de Vacaciones (AUSENCIA), excluyendo días 00:00
  const [totalVacacionesHHMM, setTotalVacacionesHHMM] = React.useState<string>('00:00');
  React.useEffect(() => {
    const calc = async () => {
      if (tipoReemplazo !== 'VACACIONES') { setTotalVacacionesHHMM('00:00'); return; }
      const ini = (ausenciaInicio || fechaInicio);
      const fin = (ausenciaFin || fechaFin);
      if (!ini || !fin || !(idsSeleccionados || []).length) { setTotalVacacionesHHMM('00:00'); return; }
      try {
        const r = await api.cupoPorDia({ asignaciones_id: idsSeleccionados, fecha_inicio: ini, fecha_fin: fin, ps_nombre: puntoSeleccionado });
        if (!r.ok) { setTotalVacacionesHHMM('00:00'); return; }
        const h2m = (v: string) => { try { const [h,m]=(v||'0:00').split(':').map(Number); return (h*60)+(m||0); } catch { return 0; } };
        const m2 = (n: number) => `${String(Math.floor(n/60)).padStart(2,'0')}:${String(n%60).padStart(2,'0')}`;
        const totalMin = (r.data || []).reduce((acc: number, d: any) => acc + Math.max(0, h2m(d.cupo_total || '0:00')), 0);
        setTotalVacacionesHHMM(m2(totalMin));
      } catch { setTotalVacacionesHHMM('00:00'); }
    };
    calc();
  }, [tipoReemplazo, idsSeleccionados, ausenciaInicio, ausenciaFin, fechaInicio, fechaFin, puntoSeleccionado]);
  // Detalle visual de cupo por día (para diagnóstico)
  const [detalleCupoDias, setDetalleCupoDias] = React.useState<Array<any>>([]);
  const [mostrarDetalleCupo, setMostrarDetalleCupo] = React.useState<boolean>(false);
  // helpers para edición de cortes por card
  const setTramoValor = (segId: string, idx: number, campo: 'ent'|'sal', valor: string) => {
    setTramosPorSeg((prev) => {
      const lista = prev[segId] ? [...prev[segId]] : [];
      if (!lista[idx]) { lista[idx] = { ent: '', sal: '' }; }
      const nuevo = { ...lista[idx], [campo]: valor };
      const nuevoMapa = { ...prev, [segId]: Object.assign([], lista, { [idx]: nuevo }) as Array<{ent:string;sal:string}> };
      return nuevoMapa;
    });
  };
  const agregarTramo = (segId: string) => {
    setTramosPorSeg((prev) => {
      const lista = prev[segId] ? [...prev[segId]] : [];
      return { ...prev, [segId]: [...lista, { ent: '', sal: '' }] };
    });
  };
  const quitarTramo = (segId: string, idx: number) => {
    setTramosPorSeg((prev) => {
      const lista = prev[segId] ? [...prev[segId]] : [];
      if (idx >= 0 && idx < lista.length) {
        lista.splice(idx, 1);
      }
      return { ...prev, [segId]: lista };
    });
  };
  const resetTramosAuto = (segId: string) => {
    const autos = (puntoSeleccionado ? getAutoTramosParaPS(puntoSeleccionado) : []).map((t) => {
      const [ent, sal] = String(t).split('-'); return { ent: (ent || '').trim(), sal: (sal || '').trim() };
    });
    setTramosPorSeg((prev) => ({ ...prev, [segId]: autos }));
  };

  // Directo: sugerir compensación si el día de cobertura no tiene asignación (ej.: cubrir en sábado)
  const [openCompForzar, setOpenCompForzar] = React.useState(false);
  const [compForzarMin, setCompForzarMin] = React.useState<string>('00:00');
  const preCheckDirectoCompensacion = React.useCallback((): boolean => {
    if (tipoReemplazo !== 'DIRECTO' || !fechaInicio || !puntoSeleccionado) return false;
    // Si AUSENCIA != COBERTURA, la compensación se aplica automáticamente por tramo.
    // Evitamos mostrar el modal forzado.
    if (ausenciaInicio && ausenciaInicio !== fechaInicio) return false;
    if ((compensaciones || []).length > 0) return false; // ya decidido compensar
    // verificar si hay asignación para el día destino en el PS actual
    const tiene = (asignaciones || [])
      .filter((a: any) => (a?.NombrePServicio || '') === puntoSeleccionado)
      .map((a: any) => getAssignmentTimesForDate(a, fechaInicio))
      .some((x: any) => x?.ent && x?.sal);
    if (tiene) return false;
    // calcular minutos totales de las cards (tramos o horaEntrada/horaSalida)
    const h2m = (v: string) => { try { const [h,m]=(v||'0:00').split(':').map(Number); return (h*60)+(m||0); } catch { return 0; } };
    let total = 0;
    for (const s of segmentos) {
      const lista = tramosPorSeg[s.id] && tramosPorSeg[s.id].length ? tramosPorSeg[s.id] : [{ ent: s.horaEntrada, sal: s.horaSalida }];
      for (const t of (lista || [])) {
        if (t?.ent && t?.sal) {
          total += Math.max(0, h2m(t.sal) - h2m(t.ent));
        }
      }
    }
    const hh = String(Math.floor(total/60)).padStart(2,'0');
    const mm = String(total%60).padStart(2,'0');
    setCompForzarMin(`${hh}:${mm}`);
    setOpenCompForzar(true);
    return true; // se abrió modal → abortar flujo actual hasta confirmar
  }, [tipoReemplazo, fechaInicio, puntoSeleccionado, asignaciones, segmentos, tramosPorSeg]);

  // validación removida: se permite que el reemplazado pueda ser reemplazante

  // util: generar fechas del rango que coinciden con los días seleccionados
  const getDatesForSelectedDays = (desde: string, hasta: string, dias: string[]): string[] => {
    try {
      if (!desde || !hasta || !dias?.length) return [];
      const out: string[] = [];
      const start = new Date(desde + 'T00:00:00');
      const end = new Date(hasta + 'T00:00:00');
      const mapIdxToName: Record<number, string> = {
        0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sabado',
      };
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const nombre = mapIdxToName[d.getDay()];
        if (dias.includes(nombre)) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          out.push(`${y}-${m}-${day}`);
        }
      }
      return out;
    } catch { return []; }
  };

  // util: nombre de día en español (formato backend) para una fecha YYYY-MM-DD
  const getNombreDia = (fechaYYYYmmdd: string): string => {
    try {
      const [y, m, d] = fechaYYYYmmdd.split('-').map((n) => parseInt(n, 10));
      const dt = new Date(y, (m - 1), d);
      const mapIdxToName: Record<number, string> = { 0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sabado' };
      return mapIdxToName[dt.getDay()];
    } catch { return ''; }
  };

  const formatFechaCorta = (iso: string): string => {
    try { const [y,m,d] = iso.split('-'); return `${d}/${m}`; } catch { return iso; }
  };

  // util: rango completo de fechas entre dos ISO (incluye extremos)
  const getAllDatesBetween = (desde: string, hasta: string): string[] => {
    try {
      if (!desde || !hasta) return [];
      const out: string[] = [];
      const start = new Date(desde + 'T00:00:00');
      const end = new Date(hasta + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        out.push(`${y}-${m}-${day}`);
      }
      return out;
    } catch { return []; }
  };

  // util: obtener ent/sal para una asignación y fecha
  const getAssignmentTimesForDate = (a: any, fechaISO: string): { ent?: string; sal?: string } => {
    try {
      // Cupo heredado: solo aporta horas dentro del rango de la cobertura de origen
      if (a?.heredada && (fechaISO < a?.vigencia_desde || fechaISO > (a?.vigencia_hasta || a?.vigencia_desde))) return {};
      const dt = new Date(fechaISO + 'T00:00:00');
      const dow = dt.getDay(); // 0=Dom..6=Sab
      const entKey = { 0: 'dom_ent', 1: 'lun_ent', 2: 'mar_ent', 3: 'mie_ent', 4: 'jue_ent', 5: 'vie_ent', 6: 'sab_ent' }[dow as 0|1|2|3|4|5|6] as string;
      const salKey = { 0: 'dom_sal', 1: 'lun_sal', 2: 'mar_sal', 3: 'mie_sal', 4: 'jue_sal', 5: 'vie_sal', 6: 'sab_sal' }[dow as 0|1|2|3|4|5|6] as string;
      const ent = a?.[entKey];
      const sal = a?.[salKey];
      return { ent, sal };
    } catch { return {}; }
  };

  // util: tramos únicos HH:mm-HH:mm para un PS en el rango de AUSENCIA
  const getAutoTramosParaPS = React.useCallback((psName: string): string[] => {
    try {
      if (!psName) return [];
      const fechas = getAllDatesBetween((ausenciaInicio || fechaInicio), (ausenciaFin || fechaFin)); // preferir ausencia para derivar tramos del PS
      if (!fechas.length) return [];
      const set = new Set<string>();
      (asignaciones || [])
        .filter((a: any) => (a?.NombrePServicio || '') === psName)
        .forEach((a: any) => {
          fechas.forEach((f) => {
            const { ent, sal } = getAssignmentTimesForDate(a, f);
            if (ent && sal) set.add(`${ent}-${sal}`);
          });
        });
      return Array.from(set);
    } catch { return []; }
  }, [asignaciones, ausenciaInicio, ausenciaFin, fechaInicio, fechaFin]);

  const ensureFechasCupo = async (modo: 'COBERTURA' | 'AUSENCIA' = 'COBERTURA') => {
    // Importante:
    // - COBERTURA: se usa para mostrar/seleccionar las fechas que el reemplazante va a cubrir ("Cubre desde/hasta").
    // - AUSENCIA: se usa cuando el origen de horas/cupo depende de la ausencia (por ejemplo, en Compensación).
    const cupoFechaInicio = (modo === 'AUSENCIA') ? (ausenciaInicio || fechaInicio) : (fechaInicio || ausenciaInicio);
    const cupoFechaFin = (modo === 'AUSENCIA') ? (ausenciaFin || fechaFin) : (fechaFin || ausenciaFin);
    if (!idsSeleccionados.length || !cupoFechaInicio || !cupoFechaFin) return;

    // cache key para evitar repetir el mismo request (mismo rango + mismos ids + mismo modo)
    const cacheKey = [
      modo,
      cupoFechaInicio,
      cupoFechaFin,
      'real_hp',
      String(operarioRemplazado || ''),
      String(puntoSeleccionado || ''),
      (idsSeleccionados || []).join(','),
      String(psIdResuelto || ''),
    ].join('|');
    if (fechasCupoCacheKeyRef.current === cacheKey && fechasCupo.length > 0) return;
    fechasCupoCacheKeyRef.current = cacheKey;

    const psid = resolvePsId();
    const cpd = await api.cupoPorDia({
      asignaciones_id: idsSeleccionados,
      fecha_inicio: cupoFechaInicio,
      fecha_fin: cupoFechaFin,
      ps_nombre: puntoSeleccionado,
      remplazado: operarioRemplazado,
      // Importante: el "máx" del modal debe ser el DISPONIBLE real por día:
      // cupo teórico - reemplazos - horas procesadas (PROCESAD*)
      modo_cupo: 'real_hp',
      ...(psIdResuelto ? { ps_id: psIdResuelto } : {}),
      ...(psid ? { ps_id: psid } : {}),
    });
    // fusionar cupo real con virtual
    const base = cpd.ok ? (cpd.data || []) : [];
    const sumHHMM = (a: string, b: string) => { try { const [ah,am]=(a||'0:00').split(':').map(Number); const [bh,bm]=(b||'0:00').split(':').map(Number); const t=(ah*60+am)+(bh*60+bm); return `${String(Math.floor(t/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`; } catch { return a||'0:00'; } };
    const virt = (tipoReemplazo === 'COMPENSACION') ? cupoVirtualPorFecha : {}; // aplicar cupo virtual solo en compensación
    const merged = base.map((d) => ({ ...d, cupo_total: (virt as any)[d.fecha] ? sumHHMM(d.cupo_total || '0:00', (virt as any)[d.fecha]) : (d.cupo_total || '0:00') }));
    setFechasCupo(merged);
  };

  const handleToggleDiaSeg = (segId: string, dayName: string) => {
    setSegmentos((prev) => prev.map((s) => {
      if (s.id !== segId) return s;
      const exists = (s.dias || []).includes(dayName);
      const nextDias = exists ? (s.dias || []).filter((d) => d !== dayName) : [ ...(s.dias || []), dayName ];
      // sincronizar fechas del mismo día si se deselecciona
      let nextFechas = s.fechas || [];
      if (exists) {
        const fechasDeEseDia = (fechasCupo || []).filter((f) => f.dia === dayName).map((f) => f.fecha);
        nextFechas = nextFechas.filter((f) => !fechasDeEseDia.includes(f));
      }
      // si se selecciona y hay varias ocurrencias, abrir modal para elegir; si es única, autoseleccionar
      if (!exists) {
        const ocurrencias = (fechasCupo || []).filter((f) => f.dia === dayName).map((f) => f.fecha);
        if (ocurrencias.length > 1) {
          setActiveSegIdForDates(segId);
          setModalDayFilter(dayName);
          setOpenFechasModal(true);
        } else if (ocurrencias.length === 1) {
          const unica = ocurrencias[0];
          if (!nextFechas.includes(unica)) nextFechas = [...nextFechas, unica];
        }
      }
      return { ...s, dias: nextDias, fechas: nextFechas, error: undefined };
    }));
  };

  const getDatesForSegment = (s: { dias: string[] }): string[] => {
    if (!s?.dias?.length) return [];
    return getDatesForSelectedDays(fechaInicio, fechaFin, s.dias);
  };

  const openModalDias = async (segId: string) => {
    // Este modal selecciona FECHAS A CUBRIR (destino), por lo que debe basarse en COBERTURA.
    await ensureFechasCupo('COBERTURA');
    setActiveSegIdForDates(segId);
    setModalDayFilter(null);
    setOpenFechasModal(true);
  };

  const handleToggleDia = async (diaNombre: string) => {
    const isSelected = diasSeleccionados.includes(diaNombre);
    if (isSelected) {
      // deseleccionar día y limpiar fechas seleccionadas correspondientes
      setDiasSeleccionados((prev) => prev.filter(x => x !== diaNombre));
      const fechasDeDia = fechasCupo.filter(f => f.dia === diaNombre).map(f => f.fecha);
      if (fechasDeDia.length) setFechasSeleccionadas((prev) => prev.filter(f => !fechasDeDia.includes(f)));
      return;
    }
    // Este selector controla FECHAS A CUBRIR (destino) => debe usar COBERTURA.
    await ensureFechasCupo('COBERTURA');
    const fechasDeDia = fechasCupo.filter(f => f.dia === diaNombre);
    if (fechasDeDia.length > 1) {
      // varios días iguales: abrir modal filtrado
      setDiasSeleccionados((prev) => [...prev, diaNombre]);
      setModalDayFilter(diaNombre);
      setOpenFechasModal(true);
    } else {
      // uno solo: marcar día y autoseleccionar su fecha
      setDiasSeleccionados((prev) => [...prev, diaNombre]);
      if (fechasDeDia.length === 1) {
        const fechaUnica = fechasDeDia[0].fecha;
        setFechasSeleccionadas((prev) => prev.includes(fechaUnica) ? prev : [...prev, fechaUnica]);
      }
    }
  };

  // util: obtener nombre y legajo a partir de "NOMBRE - LEGAJO"
  const splitNombreLegajo = (combo: string | null | undefined): { nombre: string; legajo: string } => {
    const raw = (combo || '').toString();
    const idx = raw.lastIndexOf(' - ');
    if (idx > -1) {
      return { nombre: raw.slice(0, idx).trim(), legajo: raw.slice(idx + 3).trim() };
    }
    return { nombre: raw.trim(), legajo: '' };
  };

  // util: normalizar nombre (mayúsculas, sin acentos y espacios colapsados)
  const normalizeName = (s: string | null | undefined): string => {
    return (s || '')
      .toString()
      .normalize('NFD')
      // remover marcas diacríticas (compatible con targets sin Unicode property escapes)
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase()
      .replace(/\s+/g, ' ')
      .trim();
  };

  // validar duplicado exacto y solape contra backend (reutilizable en Paso 5 y Paso 6)
  const validarAntesDeContinuar = async (): Promise<{ ok: boolean; error?: string }> => {
    try {
      // validaciones específicas por tipo de reemplazo
      if (tipoReemplazo === 'DIRECTO') { // para reemplazo directo
        if (!ausenciaInicio || !fechaInicio) {
          return { ok: false, error: 'Para Reemplazo Directo indicá un día de ausencia y un día de cobertura.' };
        }
      } // fin DIRECTO
      if (tipoReemplazo === 'VACACIONES') { // para vacaciones
        if (!fechaInicio || !fechaFin) { // rango requerido
          return { ok: false, error: 'Para el tipo Por Rango debe indicar un rango de fechas (desde y hasta).' };
        } // fin check rango
      } // fin VACACIONES

      // construir lista de segmentos efectivos (si no hay, usar el único)
      // En Vacaciones + "Cubrir todo", considerar válidos los segmentos que tengan al menos un corte editable (tramosPorSeg)
      const segs = (segmentos.length > 0
        ? segmentos
        : [{ id: 'único', operario: operarioRemplazante, horaEntrada, horaSalida } as any]
      ).filter((s: any) => {
        const tieneHorasBasicas = Boolean(s.operario && s.horaEntrada && s.horaSalida);
        if (tieneHorasBasicas) return true;
        // aceptar segmentos que poseen cortes editados en la card (tramosPorSeg) para cualquier tipo
        const tramos = tramosPorSeg[s.id] || [];
        const tieneAlgunaFila = Array.isArray(tramos) && tramos.some((t: any) => (t?.ent && t?.sal));
        return Boolean(s.operario) && tieneAlgunaFila;
      });
      if (!segs.length) return { ok: false, error: 'Complete al menos un reemplazante y su horario.' };

      const basePayload = {
        remplazado: operarioRemplazado,
        fechaInicioRemplazante: fechaInicio,
        fechaFinRemplazante: fechaFin,
        puntoServicio: puntoSeleccionado,
        dias: diasSeleccionados,
        asignaciones_id: idsSeleccionados,
      };

      // 0) Validación de cupo por día: granular por card
      // Nota: para VACACIONES delegamos la verificación íntegra al backend (por PS y día),
      // evitando falsos positivos cuando se cubre un rango completo con múltiples días/cortes.
      if (tipoReemplazo !== 'VACACIONES' && idsSeleccionados.length && (fechaInicio || ausenciaInicio) && (fechaFin || ausenciaFin)) {
        // DIRECTO especial (AUSENCIA != COBERTURA):
        // - El reemplazo se guardará para la fecha de COBERTURA.
        // - Pero el cupo que debe alcanzar es el del día de AUSENCIA.
        if (tipoReemplazo === 'DIRECTO' && ausenciaInicio && fechaInicio && ausenciaInicio !== fechaInicio) {
          const psid = resolvePsId();
          const cupo = await api.cupoPorDia({
            asignaciones_id: idsSeleccionados,
            fecha_inicio: ausenciaInicio,
            fecha_fin: ausenciaInicio,
            ps_nombre: puntoSeleccionado,
            remplazado: operarioRemplazado,
            ...(psIdResuelto ? { ps_id: psIdResuelto } : {}),
            ...(psid ? { ps_id: psid } : {}),
          });
          const limite = (cupo.ok && Array.isArray(cupo.data) && cupo.data.length)
            ? (cupo.data[0]?.cupo_total || '0:00')
            : '0:00';
          // minutos solicitados = suma de todos los tramos cargados en cards
          let minutosSolicitados = 0;
          for (const s of segs) {
            const segId = String((s as any).id || '');
            const lista = (tramosPorSeg[segId] && tramosPorSeg[segId].length)
              ? tramosPorSeg[segId]
              : [{ ent: (s as any).horaEntrada, sal: (s as any).horaSalida }];
            for (const t of (lista || [])) {
              if (t?.ent && t?.sal) {
                minutosSolicitados += Math.max(0, hhmmToMin(t.sal) - hhmmToMin(t.ent));
              }
            }
          }
          if (minutosSolicitados > hhmmToMin(limite)) {
            throw { ok: false, error: `Excede el cupo del ${ausenciaInicio} (máx ${limite}).` };
          }
        } else {
        const psid = resolvePsId();
        const cupoFechaInicio = (ausenciaInicio || fechaInicio); // usar ausencia como fuente de cupos para validación
        const cupoFechaFin = (ausenciaFin || fechaFin); // usar ausencia como fuente de cupos para validación
        const cupo = await api.cupoPorDia({ asignaciones_id: idsSeleccionados, fecha_inicio: cupoFechaInicio, fecha_fin: cupoFechaFin, ps_nombre: puntoSeleccionado, remplazado: operarioRemplazado, ...(psIdResuelto ? { ps_id: psIdResuelto } : {}), ...(psid ? { ps_id: psid } : {}) });
        if (cupo.ok && Array.isArray(cupo.data)) {
          const h2m = (hhmm: string) => { const [h,m] = (hhmm||'0:00').split(':').map(Number); return (h*60)+(m||0); };
          // integrar cupo virtual
          const mapByFecha = new Map(cupo.data.map((d: any) => {
            const virt = (tipoReemplazo === 'COMPENSACION') ? (cupoVirtualPorFecha[d.fecha] || '0:00') : '0:00';
            const sum = (() => { try { const [ah,am]=(d.cupo_total||'0:00').split(':').map(Number); const [bh,bm]=(virt||'0:00').split(':').map(Number); const t=(ah*60+am)+(bh*60+bm); return `${String(Math.floor(t/60)).padStart(2,'0')}:${String(t%60).padStart(2,'0')}`; } catch { return d.cupo_total||'0:00'; } })();
            return [d.fecha, sum];
          }));

          // construir consumo por fecha: suma de cada segmento que eligió esa fecha
          const consumoPorFecha = new Map<string, number>();
          for (const s of segs) {
            const fechasDelSeg = (s as any).fechas?.length ? (s as any).fechas : getDatesForSelectedDays(fechaInicio, fechaFin, (s as any).dias || diasSeleccionados);
            const durSeg = h2m(s.horaSalida) - h2m(s.horaEntrada);
            for (const f of fechasDelSeg) {
              consumoPorFecha.set(f, (consumoPorFecha.get(f) || 0) + Math.max(0, durSeg));
            }
          }

          consumoPorFecha.forEach((minutos, f) => {
            const limite = mapByFecha.get(f) || '0:00';
            if (minutos > h2m(limite)) {
              throw { ok: false, error: `Excede el cupo del ${f} (máx ${limite}).` };
            }
          });

          // Tope adicional por tarjeta: si un día tiene remanente 120 min y el usuario elige 14-18, bloquear por las fechas que exceden.
          // Este chequeo ya queda cubierto por el bucle anterior porque suma por fecha, pero reforzamos mensaje claro por día filtrado.
          for (const s of segs) {
            const fechasDelSeg = (s as any).fechas?.length ? (s as any).fechas : getDatesForSelectedDays(fechaInicio, fechaFin, (s as any).dias || diasSeleccionados);
            const durSeg = h2m(s.horaSalida) - h2m(s.horaEntrada);
            for (const f of fechasDelSeg) {
              const limite = mapByFecha.get(f) || '0:00';
              if (durSeg > h2m(limite)) {
                throw { ok: false, error: `El horario del segmento excede el cupo del ${f} (máx ${limite}).` };
              }
            }
          }
        }
        }
      }

      // 1) Duplicado exacto: chequear por cada segmento para el PS seleccionado y horas reales
      for (const s of segs) {
        const dup = await api.existeDuplicado({
          ...basePayload,
          remplazante: s.operario,
          horaEntradaRemplazante: s.horaEntrada,
          horaSalidaRemplazante: s.horaSalida,
        } as any);
        if (dup.ok && dup.data?.exists) {
          return { ok: false, error: `Este reemplazo ya existe para ${s.operario} (mismo PS y horario).` };
        }
      }

      // 2) Día libre detallado por fecha seleccionada por segmento
      for (const s of segs) {
        const dl = await api.verificarDiaLibre({
          ...basePayload,
          remplazante: s.operario,
          horaEntradaRemplazante: s.horaEntrada,
          horaSalidaRemplazante: s.horaSalida,
        } as any);
        if (dl.ok && dl.data?.anyLibre) {
          const fechas = dl.data.fechas?.filter((f: any) => f.libre).map((f: any) => `${f.dia} ${f.fecha}`) || [];
          const detalle = fechas.length ? ` (${fechas.join(', ')})` : '';
          return { ok: false, error: `El reemplazante ${s.operario} tiene día libre${detalle}.` };
        }
      }

      // 3) Solape general backend por segmento
      for (const s of segs) {
        const resp = await api.validarSolape({
          ...basePayload,
          remplazante: s.operario,
          horaEntradaRemplazante: s.horaEntrada,
          horaSalidaRemplazante: s.horaSalida,
        } as any);
        if (!resp.ok) return { ok: false, error: resp.error || `El reemplazante ${s.operario} posee solapes.` };
      }

      return { ok: true };
    } catch (e: any) {
      const msg = e?.error || e?.message || e?.response?.data?.error;
      if (msg) {
        return { ok: false, error: msg };
      }
      try {
        const diag = await api.diagnosticoRemplazo({
          items: construirItemsActuales(),
          fecha_ausencia: (ausenciaInicio || fechaInicio),
          ps_id: resolvePsId() || psIdResuelto,
          puntoServicio: puntoSeleccionado,
          asignaciones_id: idsSeleccionados,
          remplazado: operarioRemplazado,
        });
        const razones = diag.ok ? (diag.data?.reasons || []) : [];
        if (razones.length) {
          const detalle = `No se puede por esto:\n${razones.map((r: any) => `- ${r.message}`).join('\n')}`;
          return { ok: false, error: detalle };
        }
      } catch {}
      return { ok: false, error: 'No se pudo validar el reemplazo (duplicados/solapes). Intente nuevamente.' };
    }
  };

  // Calcular cupos (manual y automático).
  // Se reutiliza tanto por el botón como por el auto-cálculo al seleccionar/deseleccionar cortes.
  const calcularCupos = React.useCallback(async () => {
    // DIRECTO: cálculo de cupo igual a Reemplazo Rápido.
    // Objetivo: mostrar el cupo sobrante REAL para el día de AUSENCIA y el PS seleccionado,
    // considerando:
    // - Cupo teórico del ausente (asignaciones seleccionadas)
    // - Reemplazos ya cargados (V1 + V2)
    // - HorasProcesadas PROCESAD* del ausente (por PS + asignación + día)
    if (tipoReemplazo === 'DIRECTO') {
      const psid = resolvePsId();
      const fechaAusencia = (ausenciaInicio || fechaInicio);
      if (!operarioRemplazado) { setErrorPaso4('Seleccione el operario a reemplazar.'); return; }
      if (!puntoSeleccionado) { setErrorPaso4('Seleccione un Punto de Servicio.'); return; }
      if (!fechaAusencia) { setErrorPaso4('Seleccione la fecha de ausencia.'); return; }
      if (!idsSeleccionados.length) { setErrorPaso4('Seleccione al menos una asignación.'); return; }

      // Marcamos este cálculo como el más reciente y activamos loading.
      // Si llega una respuesta de un cálculo viejo, se ignora.
      const reqId = (cupoCalcReqIdRef.current += 1);
      setIsCalculatingCupos(true);

      // Helpers locales (minutos) para construir un detalle consistente
      const h2m = (v: string) => { try { const [h,m]=(v||'0:00').split(':').map(Number); return (h*60)+(m||0); } catch { return 0; } };
      const m2 = (n: number) => `${String(Math.floor(Math.max(0,n)/60)).padStart(2,'0')}:${String(Math.max(0,n)%60).padStart(2,'0')}`;

      try {
        setCupoCalculado(null);
        setDetalleCupoDias([]);

        const r = await api.cupoPorDia({
          asignaciones_id: idsSeleccionados,
          fecha_inicio: fechaAusencia,
          fecha_fin: fechaAusencia,
          ps_nombre: puntoSeleccionado,
          ...(psid ? { ps_id: psid } : {}),
          remplazado: operarioRemplazado,
          modo_cupo: 'real_hp',
        });

        // Si ya se disparó otro cálculo, ignorar este resultado.
        if (cupoCalcReqIdRef.current !== reqId) return;

        if (!r.ok) {
          setCupoCalculado({ NORMA: '00:00', TOTAL: '00:00' } as any);
          setErrorPaso4(r.error || 'Error al calcular cupo real.');
          return;
        }

        const d: any = (r.data || [])[0] || {};
        const cupo = String(d?.cupo_total || '00:00');
        const cupoTeorico = String(d?.cupo_teorico || '00:00');
        const replUsados = String(d?.reemplazos_usados || '00:00');
        const procUsados = String(d?.procesadas_usadas || '00:00');
        const usadosMin = h2m(replUsados) + h2m(procUsados);

        // Para compatibilidad con el detalle actual, guardamos total/usado/saldo,
        // y además incluimos el desglose por reemplazos y procesadas.
        setDetalleCupoDias([
          {
            fecha: d?.fecha || fechaAusencia,
            dia: d?.dia || getNombreDia(fechaAusencia),
            total: cupoTeorico,
            usado: m2(usadosMin),
            saldo: cupo,
            reemplazos_usados: replUsados,
            procesadas_usadas: procUsados,
            cupo_total: cupo,
            cupo_teorico: cupoTeorico,
          }
        ]);

        // En DIRECTO usamos el mismo valor en Normal/Total (es un solo día).
        setCupoCalculado({ NORMA: cupo, TOTAL: cupo } as any);
        setMostrarDetalleCupo(true);
        setErrorPaso4('');
      } catch {
        if (cupoCalcReqIdRef.current !== reqId) return;
        setCupoCalculado({ NORMA: '00:00', TOTAL: '00:00' } as any);
        setErrorPaso4('Error al calcular cupo real.');
      } finally {
        // Apagar loading solo si este cálculo sigue siendo el más reciente.
        if (cupoCalcReqIdRef.current === reqId) setIsCalculatingCupos(false);
      }
      return;
    }

    // VACACIONES (Por Rango): calcular cupo REAL por día (incluye HorasProcesadas) y
    // descontar además las compensaciones seleccionadas en esta UI (intra-request).
    const psid = resolvePsId();
    const inicio = (ausenciaInicio || fechaInicio);
    const fin = (ausenciaFin || fechaFin);
    const h2m = (v: string) => { try { const [h,m]=(v||'0:00').split(':').map(Number); return (h*60)+(m||0); } catch { return 0; } };
    const m2 = (n: number) => `${String(Math.floor(Math.max(0,n)/60)).padStart(2,'0')}:${String(Math.max(0,n)%60).padStart(2,'0')}`;

    // validaciones mínimas para no disparar errores al auto-calcular
    if (!operarioRemplazado) { setErrorPaso4('Seleccione el operario a reemplazar.'); return; }
    if (!puntoSeleccionado) { setErrorPaso4('Seleccione un Punto de Servicio.'); return; }
    if (!inicio || !fin) { setErrorPaso4('Seleccione un rango de fechas.'); return; }
    if (!idsSeleccionados.length) { setErrorPaso4('Seleccione al menos una asignación.'); return; }

    // Marcamos este cálculo como el más reciente y activamos loading.
    const reqId = (cupoCalcReqIdRef.current += 1);
    setIsCalculatingCupos(true);

    try {
      setCupoCalculado(null);
      setErrorPaso4('');

      // 1) Cupo real por día (teórico - reemplazos - horas procesadas)
      const cupoResp = await api.cupoPorDia({
        asignaciones_id: idsSeleccionados,
        fecha_inicio: inicio,
        fecha_fin: fin,
        ps_nombre: puntoSeleccionado,
        remplazado: operarioRemplazado,
        modo_cupo: 'real_hp',
        ...(psIdResuelto ? { ps_id: psIdResuelto } : {}),
        ...(psid ? { ps_id: psid } : {}),
      });

      // Si ya se disparó otro cálculo, ignorar este resultado.
      if (cupoCalcReqIdRef.current !== reqId) return;

      if (!cupoResp.ok) {
        setDetalleCupoDias([]);
        setCupoCalculado({ NORMA: '00:00', TOTAL: '00:00' } as any);
        setErrorPaso4(cupoResp.error || 'Error al calcular cupo real por rango.');
        return;
      }

      const cupoList = (Array.isArray(cupoResp.data) ? cupoResp.data : []) as any[];

      // 2) Compensaciones seleccionadas en UI (opción B): restan del cupo disponible
      // dentro del rango, por fecha de origen.
      const compMinPorFecha = new Map<string, number>();
      let usadoUI = 0;
      (compensaciones || []).forEach((c) => {
        const f = c?.fechaOrigen;
        if (!f) return;
        if (f < inicio || f > fin) return;
        const min = Math.max(0, h2m(c?.minutos || '00:00'));
        if (!min) return;
        usadoUI += min;
        compMinPorFecha.set(f, (compMinPorFecha.get(f) || 0) + min);
      });

      // 3) Construir detalle por día (para UI y QA)
      const detalle = cupoList.map((d: any) => {
        const fecha = String(d?.fecha || '').slice(0, 10);
        const dia = String(d?.dia || '');
        const cupoTeorico = String(d?.cupo_teorico || '00:00');
        const cupoReal = String(d?.cupo_total || '00:00');
        const replUsados = String(d?.reemplazos_usados || '00:00');
        const procUsados = String(d?.procesadas_usadas || '00:00');
        const usadosMin = h2m(replUsados) + h2m(procUsados);

        const compMin = Math.max(0, compMinPorFecha.get(fecha) || 0);
        const saldoUi = m2(Math.max(0, h2m(cupoReal) - compMin));

        return {
          fecha,
          dia,
          total: cupoTeorico,
          usado: m2(usadosMin),
          saldo: cupoReal,
          saldo_ui: saldoUi,
          reemplazos_usados: replUsados,
          procesadas_usadas: procUsados,
          comp_usada: m2(compMin),
          cupo_total: cupoReal,
          cupo_teorico: cupoTeorico,
        };
      }).filter((x) => !!x.fecha);

      setDetalleCupoDias(detalle);

      // 4) Tope por cortes seleccionados: no permitir que el cupo supere lo seleccionado en el rango.
      let maxSegunSeleccion = Number.POSITIVE_INFINITY;
      try {
        const diasParaDet = (Array.isArray(detalle) && detalle.length)
          ? detalle.map((x: any) => x.fecha)
          : (inicio ? [inicio] : []);
        const detList: any[] = [];
        for (const f of diasParaDet) {
          const cortesSeleccionadosMin = (asignaciones || [])
            .filter((a: any) => idsSeleccionados.includes(a?.id) && (a?.NombrePServicio || '') === puntoSeleccionado)
            .map((a: any) => getAssignmentTimesForDate(a, f))
            .map((t: any) => h2m(((t?.sal || '00:00')))-h2m((t?.ent || '00:00')))
            .reduce((acc, x) => acc + Math.max(0, x || 0), 0);
          detList.push({ fecha: f, cortesSeleccionadosMin });
        }
        const sumSel = detList.reduce((acc, x) => acc + (x.cortesSeleccionadosMin || 0), 0);
        if (sumSel > 0) maxSegunSeleccion = sumSel;
        setDetalleCupoDias((prev: any[]) => {
          const base = Array.isArray(prev) ? prev : [];
          return base.map((it) => {
            const found = detList.find((x) => x.fecha === it.fecha) || {};
            return { ...it, cortesSeleccionadosMin: found.cortesSeleccionadosMin };
          });
        });
      } catch {}

      // 5) Totales del rango: NORMA = cupo real (sin descontar compensaciones UI),
      // TOTAL = cupo real descontando compensaciones UI (opción B), ambos capados por selección.
      const saldoBackendMin = (detalle || []).reduce((acc: number, d: any) => acc + Math.max(0, h2m(d?.saldo || '00:00')), 0);
      const remBackend = Math.max(0, saldoBackendMin - usadoUI);
      const rem = Math.min(remBackend, isFinite(maxSegunSeleccion) ? maxSegunSeleccion : remBackend);
      const normaCapada = Math.min(saldoBackendMin, isFinite(maxSegunSeleccion) ? maxSegunSeleccion : saldoBackendMin);

      setCupoCalculado({ NORMA: m2(normaCapada), TOTAL: m2(rem) } as any);
      setMostrarDetalleCupo(true);
      setErrorPaso4('');
    } catch {
      if (cupoCalcReqIdRef.current !== reqId) return;
      setDetalleCupoDias([]);
      setCupoCalculado({ NORMA: '00:00', TOTAL: '00:00' } as any);
      setErrorPaso4('Error al calcular cupo real por rango.');
    } finally {
      if (cupoCalcReqIdRef.current === reqId) setIsCalculatingCupos(false);
    }
  }, [
    tipoReemplazo,
    api,
    asignaciones,
    idsSeleccionados,
    puntoSeleccionado,
    operarioRemplazado,
    ausenciaInicio,
    ausenciaFin,
    fechaInicio,
    fechaFin,
    compensaciones,
    psIdResuelto,
    resolvePsId,
  ]);

  // Auto-cálculo de cupos: se dispara al cambiar la selección de cortes/asignaciones
  // (y al cambiar PS/fechas/operario/tipo). Incluye debounce para evitar exceso de requests.
  const autoCalcTimerRef = React.useRef<number | undefined>(undefined);

  // Importante: evitar loops de render.
  // `calcularCupos` puede cambiar de identidad entre renders (por dependencias internas),
  // lo que haría que este useEffect se dispare en cadena y bloquee la UI.
  // Usamos un ref para invocar siempre la última versión sin depender de su identidad.
  const calcularCuposRef = React.useRef(calcularCupos);
  React.useEffect(() => {
    calcularCuposRef.current = calcularCupos;
  }, [calcularCupos]);

  React.useEffect(() => {
    // Solo para DIRECTO y Por Rango (VACACIONES), según solicitud.
    if (!(tipoReemplazo === 'DIRECTO' || tipoReemplazo === 'VACACIONES')) return;

    const ini = (ausenciaInicio || fechaInicio);
    const fin = (ausenciaFin || fechaFin);
    const faltanDatos = !idsSeleccionados.length || !ini || !fin || !puntoSeleccionado || !operarioRemplazado;

    // Si se deselecciona todo o faltan datos clave, limpiar la métrica.
    if (faltanDatos) {
      // Invalida cualquier cálculo en vuelo y apaga el loading.
      cupoCalcReqIdRef.current += 1;
      setIsCalculatingCupos(false);
      if (!idsSeleccionados.length) {
        setCupoCalculado(null);
        setDetalleCupoDias([]);
        setErrorPaso4('');
      }
      return;
    }

    // Debounce: si el usuario toca varias asignaciones rápido, se recalcula una sola vez.
    if (autoCalcTimerRef.current !== undefined) {
      window.clearTimeout(autoCalcTimerRef.current);
    }
    autoCalcTimerRef.current = window.setTimeout(() => {
      calcularCuposRef.current();
    }, 350);

    return () => {
      if (autoCalcTimerRef.current !== undefined) {
        window.clearTimeout(autoCalcTimerRef.current);
      }
    };
  }, [
    tipoReemplazo,
    idsSeleccionados,
    puntoSeleccionado,
    operarioRemplazado,
    ausenciaInicio,
    ausenciaFin,
    fechaInicio,
    fechaFin,
  ]);

  return (
    <IonContent id="reemplazos-v2" fullscreen scrollY>
      {/* encabezado a pantalla completa, fuera del contenido para evitar márgenes/bordes */}
      <Header titulo="Reemplazos" />

      {/* Loading bloqueante: evita taps mientras se recalculan cupos (auto-cálculo). */}
      <Backdrop open={isCalculatingCupos} style={{ zIndex: 2000, color: '#fff' }}>
        <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 12 }}>
          <CircularProgress color="inherit" />
          <Typography variant="body2">Calculando cupos...</Typography>
        </Box>
      </Backdrop>
      <div>
        {/* contenedor visual del módulo con fondo claro y separación del header */}
        <Box p={2} style={{ backgroundColor: 'var(--lighter-pink)', paddingBottom: 24 }}>

          {/* Pantalla de éxito: resumen y acciones para finalizar */}
          {showSuccess && (
            <Box mt={2} textAlign="center">
              <Box display="flex" justifyContent="center">
                <Paper elevation={2} style={{ padding: 16, maxWidth: 720, width: '100%' }}>
                  <Typography variant="h6" gutterBottom>Guardado exitoso</Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {successText || 'Reemplazo guardado correctamente.'}
                  </Typography>

                  {/* Resumen de lo creado */}
                  <Box mt={2} style={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2">Resumen</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Tipo: {successResumen?.tipoReemplazo || tipoReemplazo}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Ausencia: {(successResumen?.ausenciaInicio || ausenciaInicio) || '--'}{(successResumen?.ausenciaFin || ausenciaFin) ? ` a ${(successResumen?.ausenciaFin || ausenciaFin)}` : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Cobertura: {(successResumen?.coberturaInicio || fechaInicio) || '--'}{(successResumen?.coberturaFin || fechaFin) ? ` a ${(successResumen?.coberturaFin || fechaFin)}` : ''}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Punto de Servicio: {successResumen?.puntoServicio || puntoSeleccionado || '--'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Reemplazado: {successResumen?.remplazado || operarioRemplazado || '--'}
                    </Typography>

                    {(Array.isArray(successResumen?.items) && successResumen.items.length > 0) && (
                      <Box mt={1}>
                        <Typography variant="body2"><strong>Ítems creados: {successResumen.items.length}</strong></Typography>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
                          {successResumen.items.slice(0, 20).map((it: any, idx: number) => {
                            const reemplazante = it?.remplazante || '-';
                            const horario = `${it?.horaEntradaRemplazante || '--:--'} - ${it?.horaSalidaRemplazante || '--:--'}`;
                            const ps = it?.puntoServicio || successResumen?.puntoServicio || '';
                            const fechas = Array.isArray(it?.fechas) ? it.fechas : [];
                            const fechasTxt = fechas.length ? (fechas.length <= 3 ? fechas.join(', ') : `${fechas.slice(0, 3).join(', ')} (+${fechas.length - 3})`) : '--';
                            return (
                              <Paper key={`ok-${idx}`} elevation={1} style={{ padding: 10 }}>
                                <Typography variant="body2"><strong>{reemplazante}</strong> ({horario})</Typography>
                                <Typography variant="caption" color="textSecondary">PS: {ps || '--'} | Fechas: {fechasTxt}</Typography>
                              </Paper>
                            );
                          })}
                        </div>
                        {successResumen.items.length > 20 && (
                          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                            Mostrando 20 de {successResumen.items.length} ítems.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>

                  <Box mt={2} display="flex" justifyContent="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        // Finalizar: volver al home y limpiar el flujo.
                        resetAll();
                        history.push('/home');
                      }}
                    >
                      Finalizar
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        // Crear otro: limpiar el flujo sin navegar.
                        resetAll();
                      }}
                    >
                      Crear otro
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Sección: tipo de reemplazo (control global) */}
          {!showSuccess && (
            <Box mt={2} textAlign="center">
              <Box display="flex" justifyContent="center">
                <Paper elevation={1} style={{ padding: 12, maxWidth: 520, width: '100%' }}>
                  <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                    <TextField
                      select
                      variant="outlined"
                      label="Tipo de reemplazo"
                      value={tipoReemplazo}
                      onChange={(e) => setTipoReemplazo(e.target.value as string)}
                      fullWidth
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                    >
                      {tiposReemplazo.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </TextField>

                    <Typography variant="caption" color="textSecondary">
                      {tipoReemplazo === 'DIRECTO'
                        ? 'Directo: el cupo se calcula por día según las asignaciones seleccionadas.'
                        : 'Por Rango: se calcula sobre el rango y se ajusta según lo seleccionado.'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}

          {/* Modal: Compensación (flujo 1: origen, 2: destino) */}
          <Dialog
            open={openCompModal}
            onClose={() => {
              setOpenCompModal(false);
              setCompError('');
              setCompStep(1);
              setOrigenFecha('');
              setOrigenMin('00:00');
              setDestFechas([]);
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Compensación</DialogTitle>
            <DialogContent dividers>
              {compStep === 1 ? (
                <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                  <Typography variant="body2">
                    Elegí un día de AUSENCIA (origen). Se tomará el saldo disponible de ese día.
                  </Typography>

                  <TextField
                    select
                    variant="outlined"
                    label="Día origen (Ausencia)"
                    value={origenFecha}
                    onChange={(e) => {
                      const f = e.target.value as string;
                      setOrigenFecha(f);
                      const s = (saldoOrigen || []).find((x) => x.fecha === f)?.saldo || '00:00';
                      setOrigenMin(s);
                      setCompError('');
                    }}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="" disabled>
                      Seleccione un día
                    </option>
                    {(saldoOrigen || []).map((x) => (
                      <option key={x.fecha} value={x.fecha}>
                        {x.fecha} (saldo {x.saldo})
                      </option>
                    ))}
                  </TextField>

                  <Typography variant="caption" color="textSecondary">
                    Saldo seleccionado: {origenMin}
                  </Typography>
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
                  <Typography variant="body2">
                    Elegí el día de COBERTURA (destino) donde se aplicará la compensación.
                  </Typography>

                  <TextField
                    select
                    variant="outlined"
                    label="Día destino (Cobertura)"
                    value={destFechas?.[0] || ''}
                    onChange={(e) => {
                      const f = e.target.value as string;
                      setDestFechas(f ? [f] : []);
                      setCompError('');
                    }}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="" disabled>
                      Seleccione un día
                    </option>
                    {(fechasCupoDestinoComp && fechasCupoDestinoComp.length ? fechasCupoDestinoComp : fechasCupo).map((x) => (
                      <option key={x.fecha} value={x.fecha}>
                        {x.fecha}
                      </option>
                    ))}
                  </TextField>

                  <Typography variant="caption" color="textSecondary">
                    Origen: {origenFecha} | Minutos a aplicar: {origenMin}
                  </Typography>
                </Box>
              )}

              {compError ? (
                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
                  {compError}
                </Typography>
              ) : null}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setOpenCompModal(false);
                  setCompError('');
                  setCompStep(1);
                  setOrigenFecha('');
                  setOrigenMin('00:00');
                  setDestFechas([]);
                }}
              >
                Cancelar
              </Button>

              {compStep === 1 ? (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => {
                    if (!origenFecha) {
                      setCompError('Elegí un día origen.');
                      return;
                    }
                    setDestFechas([]);
                    setCompStep(2);
                  }}
                >
                  Siguiente
                </Button>
              ) : (
                <Button
                  color="primary"
                  variant="contained"
                  onClick={async () => {
                    if (destFechas.length !== 1) {
                      setCompError('Elegí un (1) día destino.');
                      return;
                    }
                    const destino = destFechas[0];
                    const h2m = (v: string) => {
                      try {
                        const [h, m] = (v || '0:00').split(':').map(Number);
                        return (h * 60) + (m || 0);
                      } catch {
                        return 0;
                      }
                    };
                    const m2hhmm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
                    const saldoSel = (saldoOrigen || []).find((x) => x.fecha === origenFecha)?.saldo || '00:00';
                    const yaUsadoOrigenMin = (compensaciones || [])
                      .filter((c) => c.fechaOrigen === origenFecha)
                      .reduce((acc, c) => acc + h2m(c.minutos), 0);
                    const disponibleOrigenMin = Math.max(0, h2m(saldoSel) - yaUsadoOrigenMin);
                    if (disponibleOrigenMin <= 0) {
                      setCompError('Ese día origen ya no tiene saldo disponible.');
                      return;
                    }
                    const yaExistePar = (compensaciones || []).some((c) => c.fechaOrigen === origenFecha && c.fechaDestino === destino);
                    if (yaExistePar) {
                      setCompError('Ya agregaste esa compensación (mismo origen y destino).');
                      return;
                    }
                    const minutosAplicarHHMM = m2hhmm(disponibleOrigenMin);
                    const psid = resolvePsId();
                    const resp = await (api as any).compensacionReservar?.({
                      remplazado: operarioRemplazado,
                      ps_nombre: puntoSeleccionado,
                      ...(psid ? { ps_id: psid } : {}),
                      fecha_origen: origenFecha,
                      fecha_destino: destino,
                      minutos: minutosAplicarHHMM,
                    });
                    if (!resp?.ok) {
                      setCompError(resp?.error || 'No se pudo reservar.');
                      return;
                    }
                    const addHHMM = (a: string, b: string) => {
                      try {
                        const [ah, am] = (a || '0:00').split(':').map(Number);
                        const [bh, bm] = (b || '0:00').split(':').map(Number);
                        const t = (ah * 60 + am) + (bh * 60 + bm);
                        return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
                      } catch {
                        return a || '0:00';
                      }
                    };
                    setCupoVirtualPorFecha((prev) => ({ ...prev, [destino]: addHHMM(prev[destino] || '00:00', minutosAplicarHHMM) }));
                    setCompensaciones((prev) => ([...prev, { fechaOrigen: origenFecha, fechaDestino: destino, minutos: minutosAplicarHHMM }]));
                    await ensureFechasCupo();
                    setOpenCompModal(false);
                    setCompError('');
                    setCompStep(1);
                    setOrigenFecha('');
                    setOrigenMin('00:00');
                    setDestFechas([]);
                  }}
                >
                  Confirmar
                </Button>
              )}
            </DialogActions>
          </Dialog>

        {/* Modal: sugerir Compensación cuando Directo cubre un día sin asignación */}
        <Dialog open={openCompForzar} onClose={() => setOpenCompForzar(false)} fullWidth maxWidth="sm">
          <DialogTitle>¿Convertir a compensación?</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2">
              El día de cobertura seleccionado no tiene asignación en el Punto de Servicio. ¿Deseás compensar
              usando las horas del día de ausencia?
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
              Se trasladarán {compForzarMin} desde {formatFechaCorta(ausenciaInicio || fechaInicio)} a {formatFechaCorta(fechaInicio || '')}.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCompForzar(false)}>No</Button>
            <Button color="primary" variant="contained" onClick={() => {
              const minutos = compForzarMin || '00:00';
              // 1. Establecer compensación en estado
              setCompensaciones([{ fechaOrigen: ausenciaInicio, fechaDestino: fechaInicio, minutos }]);
              
              // 2. Rellenar horas por defecto si faltan (07:00 a 07:00 + min)
              setSegmentos((prev) => prev.map((s) => {
                if (s.horaEntrada && s.horaSalida) return s;
                const addMin = (hhmm: string, add: string) => {
                  try {
                    const [h,m]=(hhmm||'00:00').split(':').map(Number);
                    const [ah,am]=(add||'00:00').split(':').map(Number);
                    const t=(h*60+m)+(ah*60+am);
                    const hh=String(Math.floor(t/60)%24).padStart(2,'0');
                    const mm=String(t%60).padStart(2,'0');
                    return `${hh}:${mm}`;
                  } catch { return hhmm || '00:00'; }
                };
                const ent = '07:00';
                const sal = addMin(ent, minutos);
                return { ...s, horaEntrada: ent, horaSalida: sal };
              }));

              setOpenCompForzar(false);
              setOpenConfirmModal(true);
            }}>Sí, compensar</Button>
          </DialogActions>
        </Dialog>
        {/* Sección: operario a reemplazar */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: 12, width: '100%' }}>
              <Box style={{ width: '100%', maxWidth: 320 }}>
                <Autocomplete
                  options={operarios}
                  value={operarioRemplazado}
                  onChange={(_, v) => setOperarioRemplazado(v)}
                  getOptionLabel={(option) => option || ''}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const texto = inputValue.toLowerCase();
                    return options.filter((op) => op.toLowerCase().includes(texto));
                  }}
                  onOpen={async () => {
                    if (operarios.length === 0) {
                      const res = await api.listarOperarios();
                      if (res.ok) setOperarios(res.data || []);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Operario a reemplazar" fullWidth />
                  )}
                />
              </Box>
              <Box style={{ width: '100%', maxWidth: 320 }}>
                <TextField
                  select
                  variant="outlined"
                  label="Motivo"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value as string)}
                  fullWidth
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="" disabled>Seleccione un motivo</option>
                  {motivos.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </TextField>
              </Box>
            </Box>
            {errorPaso1 ? (
              <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorPaso1}</Typography>
            ) : null}
          </Box>
        )}

        {/* Sección: rango de fechas (Ausencia y Cobertura) */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            {/* Bloque: Fecha de ausencia */}
            <Typography variant="subtitle2" className="mb-2">Fecha de ausencia</Typography>
            <Box display="flex" justifyContent="center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <DatePicker
                autoOk
                variant="dialog"
                inputVariant="outlined"
                format="DD/MM/YYYY"
                label={tipoReemplazo === 'DIRECTO' ? 'Ausencia' : 'Ausencia desde'}
                value={ausenciaInicio ? new Date(`${ausenciaInicio}T00:00:00`) : null}
                onChange={(val: any) => {
                  try {
                    const d: any = val;
                    const iso = d?.toDate ? d.toDate().toISOString() : d?._d?.toISOString?.();
                    setAusenciaInicio(iso ? iso.slice(0, 10) : ausenciaInicio);
                    if (tipoReemplazo === 'DIRECTO') { // sincronizar fin = inicio
                      setAusenciaFin(iso ? iso.slice(0, 10) : ausenciaInicio);
                    }
                  } catch {}
                }}
              />
              {tipoReemplazo !== 'DIRECTO' && (
                <DatePicker
                  autoOk
                  variant="dialog"
                  inputVariant="outlined"
                  format="DD/MM/YYYY"
                  label="Ausencia hasta"
                  value={ausenciaFin ? new Date(`${ausenciaFin}T00:00:00`) : null}
                  onChange={(val: any) => {
                    try {
                      const d: any = val;
                      const iso = d?.toDate ? d.toDate().toISOString() : d?._d?.toISOString?.();
                      setAusenciaFin(iso ? iso.slice(0, 10) : ausenciaFin);
                    } catch {}
                  }}
                />
              )}
            </Box>

            {/* Bloque: Fecha en que cubre */}
            <Typography variant="subtitle2" className="mb-2" style={{ marginTop: 8 }}>Fecha en que cubre</Typography>
            <Box display="flex" justifyContent="center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <DatePicker
                autoOk
                variant="dialog"
                inputVariant="outlined"
                format="DD/MM/YYYY"
                label={tipoReemplazo === 'DIRECTO' ? 'Cubre' : 'Cubre desde'}
                value={fechaInicio ? new Date(`${fechaInicio}T00:00:00`) : null}
                onChange={(val: any) => {
                  try {
                    const d: any = val;
                    const iso = d?.toDate ? d.toDate().toISOString() : d?._d?.toISOString?.();
                    setFechaInicio(iso ? iso.slice(0, 10) : fechaInicio);
                    if (tipoReemplazo === 'DIRECTO') { // sincronizar fin = inicio
                      setFechaFin(iso ? iso.slice(0, 10) : fechaInicio);
                    }
                  } catch {}
                }}
              />
              {tipoReemplazo !== 'DIRECTO' && (
                <DatePicker
                  autoOk
                  variant="dialog"
                  inputVariant="outlined"
                  format="DD/MM/YYYY"
                  label="Cubre hasta"
                  value={fechaFin ? new Date(`${fechaFin}T00:00:00`) : null}
                  onChange={(val: any) => {
                    try {
                      const d: any = val;
                      const iso = d?.toDate ? d.toDate().toISOString() : d?._d?.toISOString?.();
                      setFechaFin(iso ? iso.slice(0, 10) : fechaFin);
                    } catch {}
                  }}
                />
              )}
            </Box>
            {errorPaso2 ? (
              <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorPaso2}</Typography>
            ) : null}

            {/* Vacaciones: se fuerza cubrir todo; mostrar total de horas del rango */}
            {tipoReemplazo === 'VACACIONES' && (
              <Box mt={1} display="flex" flexDirection="column" alignItems="center" style={{ gap: 6 }}>
                <Typography variant="caption" color="textSecondary">
                  Se cubren automáticamente todas las asignaciones del rango. Podés dividir fechas por reemplazante.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Sección: punto de servicio + reemplazos existentes */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            <Box display="flex" flexDirection="column" alignItems="center">
              <Box style={{ width: '100%', maxWidth: 360 }}>
                <TextField
                  select
                  variant="outlined"
                  label="Punto de Servicio"
                  value={puntoSeleccionado}
                  onChange={(e) => {
                    const nextPs = (e.target.value as string) || '';
                    // Al cambiar el PS, invalidar selecciones/cupos para evitar estado inconsistente
                    // (ej: cupo calculado para otro PS pero la lista muestra botones sin seleccionar).
                    setPuntoSeleccionado(nextPs);
                    setIdsSeleccionados([]);
                    setCupoCalculado(null);
                    setDetalleCupoDias([]);
                    setErrorPaso4('');
                    // invalidar cache de fechas de cupo (modal de fechas)
                    fechasCupoCacheKeyRef.current = '';
                    setFechasCupo([]);
                  }}
                  fullWidth
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="" disabled>Seleccione un Punto de Servicio</option>
                  {puntosServicio.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </TextField>
              </Box>
              {/* listado visual (auto-cargado) */}
              <Typography variant="subtitle2" className="mb-2" style={{ marginTop: 12 }}>Reemplazos existentes</Typography>
              {reemplazosExistentes.length > 0 && (
                <>
                  <Typography variant="subtitle2" className="mb-2">Reemplazos encontrados ({reemplazosExistentes.length})</Typography>
                  <div style={{ width: '100%', maxWidth: 840, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8, paddingRight: 8 }}>
                  {reemplazosExistentes.map((r: any, idx: number) => {
                    const reemplazante = r?.nombre_remplazo || r?.remplazante || '';
                    const rango = (ini?: string, fin?: string) => (ini ? `${formatFechaCorta(ini)}${fin && fin !== ini ? ` al ${formatFechaCorta(fin)}` : ''}` : '');
                    const cobertura = rango(r?.fecha_inicio, r?.fecha_fin);
                    // Día del que sale la hora: fecha de ausencia (directo) o rango de ausencia (por rango)
                    const ausencia = r?.fecha_ausencia
                      ? formatFechaCorta(r.fecha_ausencia)
                      : rango(r?.fecha_ini_ausencia, r?.fecha_fin_ausencia);
                    const compensa = (Array.isArray(r?.compensacion_detalle) ? r.compensacion_detalle : [])
                      .filter((c: any) => c?.origen)
                      .map((c: any) => `${formatFechaCorta(c.origen)} (${minToHHmm(c.minutos)})`)
                      .join(', ');
                    const tramos = getTramosDeReemplazo(r);
                    const horario = tramos.length ? tramos.join(', ') : '--';
                    const ps = r?.nombre_ps || r?.ps_nombre || '';
                    const esEmsysV1 = r?.fuente === 'REMPLAZOSDET';
                    const tipo = esEmsysV1 ? 'EMSYS' : (r?.tipo_reemplazo_v2 || '--');
                    const filas: Array<[string, string]> = [
                      ['Reemplazante', reemplazante || '--'],
                      ['Ausencia', ausencia || '--'],
                      ['Cubre', cobertura || '--'],
                      ...(compensa ? [['Usa horas del', compensa] as [string, string]] : []),
                      ['Horario', horario],
                      ['Punto de Serv', ps || '--'],
                      ['Tipo', tipo],
                    ];
                    return (
                      <Paper key={`${r?.fuente || ''}-${r?.id || idx}`} elevation={1} style={{ padding: 12, opacity: ps && puntoSeleccionado && ps !== puntoSeleccionado ? 0.7 : 1 }}>
                        <Box display="flex" style={{ gap: 12 }}>
                          <Box flex={1} style={{ textAlign: 'left', minWidth: 0 }}>
                            {filas.map(([label, valor]) => (
                              <Box key={label} display="flex" style={{ gap: 8 }}>
                                <Typography variant="body2" style={{ width: 110, flexShrink: 0 }}><strong>{label}</strong></Typography>
                                <Typography variant="body2" style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{valor}</Typography>
                              </Box>
                            ))}
                          </Box>
                          {!esEmsysV1 && (
                          <Box>
                          <Button
                            size="small"
                            variant="outlined"
                            color="default"
                            style={{ color: 'var(--ion-color-danger)', borderColor: 'var(--ion-color-danger)' }}
                              onClick={async () => {
                                if (!r?.id) return;
                                const ok = window.confirm('¿Eliminar este reemplazo? Esta acción no se puede deshacer.');
                                if (!ok) return;
                                try {
                                  const res = await apiEliminarReemplazo({ id: r.id });
                                  if (!res.ok) { alert(res.error || 'No se pudo eliminar el reemplazo'); return; }
                                  // refrescar listando de nuevo con filtros vigentes
                                  const payload = payloadListadoExistentes();
                                  const refresh = payload ? await api.listarReemplazosExistentes(payload) : { ok: true, data: [] };
                                  setReemplazosExistentes(refresh.ok ? (refresh.data || []) : []);
                                } catch {}
                              }}
                            >
                              Eliminar
                            </Button>
                          </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
                  </div>
                </>
              )}
              {errorListado ? (
                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorListado}</Typography>
              ) : null}
              {errorPaso3 ? (
                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorPaso3}</Typography>
              ) : null}
            </Box>
          </Box>
        )}

        {/* Sección: asignaciones y cupos */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            <Box style={{ maxWidth: 840, margin: '0 auto' }}>
              {/* lista de asignaciones del PS seleccionado (oculta cuando se cubre todo) */}
              {!(tipoReemplazo === 'VACACIONES' && vacacionesCubrirTodo) && (
                <Box mb={2}>
                  {asignaciones.filter((a: any) => a?.NombrePServicio === puntoSeleccionado).map((a: any) => {
                    // DIRECTO: mostrar el tramo del día de AUSENCIA (no el set de toda la semana)
                    const fechaReferencia = (tipoReemplazo === 'DIRECTO') ? (ausenciaInicio || fechaInicio) : '';
                    const tramoDia = (fechaReferencia ? getAssignmentTimesForDate(a, fechaReferencia) : {}) as any;
                    const tramos = (fechaReferencia && tramoDia?.ent && tramoDia?.sal)
                      ? [`${tramoDia.ent}-${tramoDia.sal}`]
                      : getTramosAsignacion(a);
                    const texto = tramos.length ? tramos.join(', ') : `Asignación #${a.id}`;
                    const vigencia = a?.heredada
                      ? (a.vigencia_desde === a.vigencia_hasta
                        ? formatFechaCorta(a.vigencia_desde)
                        : `${formatFechaCorta(a.vigencia_desde)} al ${formatFechaCorta(a.vigencia_hasta)}`)
                      : '';
                    return (
                      <Box key={a.id} display="flex" alignItems="center" justifyContent="space-between" style={{ border: a?.heredada ? '1px dashed var(--ion-color-warning)' : '1px solid rgba(0,0,0,0.12)', borderRadius: 4, padding: 8, marginBottom: 8, gap: 8 }}>
                        <Box style={{ textAlign: 'left', minWidth: 0 }}>
                          <Typography variant="body2">{texto}</Typography>
                          {a?.heredada && (
                            <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                              Cupo heredado: cubre a {a.titular_nombre || 'otro operario'} ({vigencia}){a.fuente === 'REMPLAZOSDET' ? ' · EMSYS' : ''}
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant={idsSeleccionados.includes(a.id) ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => {
                            // El cupo heredado (id negativo) no se mezcla con asignaciones propias:
                            // al elegir de un tipo se descarta la selección del otro.
                            const esHeredada = !!a?.heredada;
                            setIdsSeleccionados((prev) => prev.includes(a.id)
                              ? prev.filter((x) => x !== a.id)
                              : [...prev.filter((x) => (x < 0) === esHeredada), a.id]);
                            setCupoCalculado(null);
                            setDetalleCupoDias([]);
                            setErrorPaso4('');
                          }}
                        >
                          {idsSeleccionados.includes(a.id) ? 'Seleccionada' : 'Seleccionar'}
                        </Button>
                      </Box>
                    );
                  })}
                  {!asignaciones.filter((a: any) => a?.NombrePServicio === puntoSeleccionado).length && (
                    <Typography variant="body2" color="textSecondary">No hay asignaciones para el Punto de Servicio seleccionado.</Typography>
                  )}
                </Box>
              )}

              {/* Cupos: cálculo automático al seleccionar/deseleccionar cortes (con debounce). */}

              {/* métrica de cupos */}
              {cupoCalculado && (
                <Box display="flex" justifyContent="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                  {tipoReemplazo === 'DIRECTO' ? (
                    (() => {
                      const d: any = (Array.isArray(detalleCupoDias) && detalleCupoDias.length) ? (detalleCupoDias[0] || {}) : {};
                      // Cupo Total: valor teórico (antes de descuentos) del día de ausencia.
                      const cupoTotal = String(d?.total || d?.cupo_teorico || '00:00');

                      // Horas Procesadas: consumido total = procesadas (asignaciones) + reemplazos.
                      // En el detalle ya se guarda como `usado` para evitar recalcular.
                      const horasProcesadas = String(d?.usado || '00:00');

                      // Cupo Disponible: saldo/cupo_total real del día (real_hp)
                      const libre = String(d?.saldo || d?.cupo_total || cupoCalculado?.TOTAL || '00:00');

                      return (
                        <>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Cupo Total</Typography>
                            <Typography variant="h6" color="primary">{cupoTotal}</Typography>
                          </Paper>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Horas Procesadas</Typography>
                            <Typography variant="h6" color="primary">{horasProcesadas}</Typography>
                          </Paper>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Cupo Disponible</Typography>
                            <Typography variant="h6" color="primary">{libre}</Typography>
                          </Paper>
                        </>
                      );
                    })()
                  ) : (
                    (() => {
                      const detalle = Array.isArray(detalleCupoDias) ? detalleCupoDias : [];

                      // Cupo Total (rango): suma del cupo teórico por día (según asignaciones seleccionadas).
                      const cupoTotalMin = detalle.reduce((acc: number, it: any) => acc + hhmmToMin(String(it?.total || it?.cupo_teorico || '00:00')), 0);
                      const cupoTotal = minToHHmm(cupoTotalMin);

                      // Horas Procesadas (rango): consumo = reemplazos_usados + procesadas_usadas por día.
                      // En el detalle ya queda guardado como `usado`.
                      const horasProcesadasMin = detalle.reduce((acc: number, it: any) => acc + hhmmToMin(String(it?.usado || '00:00')), 0);
                      const horasProcesadas = minToHHmm(horasProcesadasMin);

                      // Cupo Disponible (rango): valor final ya con compensaciones UI (opción B) y topes.
                      const libre = String(cupoCalculado?.TOTAL || '00:00');

                      return (
                        <>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Cupo Total</Typography>
                            <Typography variant="h6" color="primary">{cupoTotal}</Typography>
                          </Paper>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Horas Procesadas</Typography>
                            <Typography variant="h6" color="primary">{horasProcesadas}</Typography>
                          </Paper>
                          <Paper elevation={1} style={{ padding: 12, minWidth: 120 }}>
                            <Typography variant="caption" color="textSecondary">Cupo Disponible</Typography>
                            <Typography variant="h6" color="primary">{libre}</Typography>
                          </Paper>
                        </>
                      );
                    })()
                  )}
                </Box>
              )}

              {/* El cálculo de cupos es automático; no se muestra botón manual. */}

              {errorPaso4 ? (
                <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorPaso4}</Typography>
              ) : null}

              {/* NUEVO: Tarjeta de Compensación (solo visible si el tipo es COMPENSACION) */}
              {false && (
                <Box mt={3}>
                  <Paper elevation={1} style={{ padding: 12 }}>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Compensación (usar horas de otro día)</Typography>
                    {compensaciones.length > 0 && (
                      <Box mb={1}>
                        {compensaciones.map((c, i) => (
                          <Typography key={`${c.fechaOrigen}-${c.fechaDestino}-${i}`} variant="body2">
                            Usás {c.minutos} de {formatFechaCorta(c.fechaOrigen)} → {formatFechaCorta(c.fechaDestino)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                    disabled={!puntoSeleccionado || !(ausenciaInicio || fechaInicio) || !(ausenciaFin || fechaFin)}
                      onClick={async () => {
                        setCompError('');
                        setCompStep(1);
                        setOpenCompModal(true);
                        // cargar saldos desde backend; si no hay, usar cupoPorDia como fallback
                      const psid = resolvePsId();
                      const res = await (api as any).compensacionSaldoOrigen?.({ remplazado: operarioRemplazado, fecha_inicio: (ausenciaInicio || fechaInicio), fecha_fin: (ausenciaFin || fechaFin), ps_nombre: puntoSeleccionado, ...(psid ? { ps_id: psid } : {}) });
                        const lista = res?.ok ? (res.data || []) : [];
                        if (lista.length > 0) setSaldoOrigen(lista);
                        else {
                        try {
                          const cupoFechaInicio = (ausenciaInicio || fechaInicio);
                          const cupoFechaFin = (ausenciaFin || fechaFin);
                          const cpd = await api.cupoPorDia({ asignaciones_id: idsSeleccionados, fecha_inicio: cupoFechaInicio, fecha_fin: cupoFechaFin, ps_nombre: puntoSeleccionado, ...(psid ? { ps_id: psid } : {}) });
                            const saldos = (cpd.ok ? (cpd.data || []) : []).map((d: any) => ({ fecha: d.fecha, dia: d.dia, saldo: d.cupo_total || '00:00' }));
                            setSaldoOrigen(saldos);
                          } catch { setSaldoOrigen([]); }
                        }
                      }}
                    >
                      Usar horas de otro día
                    </Button>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                      Debe ser el mismo Punto de Servicio. Mínimo 15 minutos.
                    </Typography>
                  </Paper>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Sección: horario y días (segmentos) */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            {/* Lista de segmentos múltiples (scroll en área dedicada, con alto controlado) */}
            <Box style={{ maxWidth: 840, margin: '0 auto' }}>
              <div ref={segListRef} className="reemplazo-carousel" style={{ width: '100%', margin: '0 auto', display: 'flex', flexWrap: 'nowrap', gap: '12px', overflowX: 'scroll', overflowY: 'hidden', WebkitOverflowScrolling: 'touch' }}>
              {segmentos.map((s, idx) => {
                // permitir mismo operario en múltiples tarjetas (trabajos por corte)
                const opciones = operarios;
                return (
                  <Paper
                    key={s.id}
                    elevation={1}
                    className="reemplazo-card"
                    style={{
                      padding: 10, // espaciado interno de la card
                      border: '1px solid rgba(0,0,0,0.08)', // borde suave
                      borderRadius: 16, // esquinas redondeadas
                      background: '#fff', // fondo blanco
                      overflow: 'visible', // mostrar popovers
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)', // sombra suave
                      flex: '0 0 320px', // ancho fijo dentro del carrusel
                      maxWidth: 320 // límite máximo para evitar crecimientos
                    }}
                  >
                    {/* Fila 1: Reemplazante (70%) + Quitar (derecha) */}
                    <Box className="row-top" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 8, alignItems: 'center', width: '100%' }}>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                          options={opciones}
                          value={s.operario}
                          onChange={(_, v) => setSegmentos((prev) => prev.map((p) => p.id === s.id ? { ...p, operario: v } : p))}
                          getOptionLabel={(option) => option || ''}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            const texto = inputValue.toLowerCase();
                            return options.filter((op) => op.toLowerCase().includes(texto));
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              margin="dense"
                              size="small"
                              label={`Reemplazante #${idx + 1}`}
                              fullWidth
                              InputProps={{ ...params.InputProps, notched: false, style: { height: fieldHeight, fontSize: 14 } }}
                            />
                          )}
                        />
                      </Box>
                      <Button size="small" variant="outlined" style={{ borderColor: 'var(--garnet)', color: 'var(--garnet)', height: fieldHeight, minWidth: 84 }} onClick={() => setSegmentos((prev) => prev.filter((p) => p.id !== s.id))}>Quitar</Button>
                    </Box>
                    {/* Fila 2: Horarios */}
                    { (false) ? (
                      <Box className="row-bottom" style={{ width: '100%', display: 'block' }}> {/* asegurar una sola columna */}
                        {/* Mostrar cortes automáticos por PS (si hay PS seleccionado) */}
                        {puntoSeleccionado ? (
                          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                            {(getAutoTramosParaPS(puntoSeleccionado) || []).map((t) => (
                              <Paper key={`${s.id}-${t}`} elevation={0} style={{ padding: '4px 8px' }}><Typography variant="body2">{t}</Typography></Paper>
                            ))}
                            {getAutoTramosParaPS(puntoSeleccionado).length === 0 && (
                              <Typography variant="caption" color="textSecondary">Sin cortes para el PS seleccionado en el rango de ausencia.</Typography>
                            )}
                            <Button size="small" onClick={() => setEditHorasSegIds((prev) => prev.includes(s.id) ? prev : [...prev, s.id])}>
                              Editar horario
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="textSecondary">Seleccione un Punto de Servicio para ver los cortes del operario en el rango de ausencia.</Typography>
                        )}
                      </Box>
                    ) : (
                      <Box className="row-bottom" style={{ width: '100%', display: 'block' }}> {/* forzar layout de una sola columna dentro de la card */}
                        { (tipoReemplazo === 'VACACIONES') ? ( // mostrar grilla siempre que sea VACACIONES, sin depender de "Cubrir todo"
                          <>
                            {(() => {
                              // construir filas: priorizar las editadas en la card
                              // usar cortes automáticos SOLO cuando "Cubrir todo" esté activo; en caso contrario, empezar con una fila vacía
                              const autos = (vacacionesCubrirTodo && puntoSeleccionado)
                                ? (getAutoTramosParaPS(puntoSeleccionado) || []).map((t) => {
                                    const [e, a] = String(t).split('-'); // separar entrada - salida
                                    return { ent: (e || '').trim(), sal: (a || '').trim() }; // normalizar espacios
                                  })
                                : []; // sin autos si no se cubre todo
                              const base = (tramosPorSeg[s.id] && tramosPorSeg[s.id].length ? tramosPorSeg[s.id] : autos); // filas base
                              const filas = (base && base.length ? base : [{ ent: '', sal: '' }]); // asegurar al menos 1 fila editable
                              return (
                                <>
                                  {/* Contenedor de una sola columna: título arriba, luego filas una debajo de otra y al final acciones */}
                                  <Box style={{ display: 'flex', flexDirection: 'column', rowGap: 8, width: '100%' }}>
                                    {/* fila de títulos */}
                                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 8, width: '100%' }}>
                                      <Typography variant="caption">Hora entrada</Typography> {/* título columna izquierda */}
                                      <Typography variant="caption">Hora salida</Typography> {/* título columna derecha */}
                                    </Box>
                                    {/* filas de edición */}
                                    {filas.map((tr, idx) => (
                                      <Box key={`${s.id}-tr-${idx}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) 24px', columnGap: 8, alignItems: 'center', width: '100%' }}>
                                        <TextField
                                          type="time" // entrada de tiempo HH:mm
                                          variant="outlined" // estilo material
                                          size="small" // tamaño compacto
                                          fullWidth // ocupar ancho de la celda
                                          style={{ width: '100%', minWidth: 0 }} // permitir que se adapte al ancho disponible
                                          value={tr.ent || ''} // valor de entrada
                                          onChange={(e) => setTramoValor(s.id, idx, 'ent', e.target.value)} // actualizar estado
                                          inputProps={{ style: { padding: '6px 8px' } }} // padding consistente
                                        />
                                        <TextField
                                          type="time" // salida de tiempo HH:mm
                                          variant="outlined" // estilo material
                                          size="small" // tamaño compacto
                                          fullWidth // ocupar ancho de la celda
                                          style={{ width: '100%', minWidth: 0 }} // permitir que se adapte al ancho disponible
                                          value={tr.sal || ''} // valor de salida
                                          onChange={(e) => setTramoValor(s.id, idx, 'sal', e.target.value)} // actualizar estado
                                          inputProps={{ style: { padding: '6px 8px' } }} // padding consistente
                                        />
                                        <IconButton
                                          aria-label="Eliminar corte" // accesibilidad
                                          size="small" // tamaño compacto
                                          onClick={() => quitarTramo(s.id, idx)} // eliminar la fila/corte actual
                                          style={{ color: 'var(--garnet)', justifySelf: 'center' }} // color institucional y centrado en su columna
                                        >
                                          <DeleteOutlineIcon fontSize="small" /> {/* icono de eliminar */}
                                        </IconButton>
                                      </Box>
                                    ))}
                                    {/* acciones abajo ocupando el ancho completo */}
                                    <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: 8, marginTop: 8, width: '100%' }}>
                                      <Button
                                        size="small" // tamaño compacto
                                        fullWidth // ocupar toda la columna
                                        variant="outlined" // estilo con contorno
                                        style={{ borderColor: 'var(--garnet)', color: 'var(--garnet)', whiteSpace: 'nowrap', textTransform: 'none' }} // borde, texto y evitar salto de línea
                                        onClick={() => agregarTramo(s.id)} // agregar una nueva fila de corte
                                      >
                                        Agregar corte
                                      </Button>
                                      {/* Se eliminan Auto y Cerrar en card de reemplazante */}
                                    </Box>
                                  </Box>
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            {(() => {
                              // construir filas para cualquier tipo: preferir las editadas en la card; si no, usar valores del segmento
                              const base = (tramosPorSeg[s.id] && tramosPorSeg[s.id].length)
                                ? tramosPorSeg[s.id]
                                : (s.horaEntrada || s.horaSalida ? [{ ent: s.horaEntrada, sal: s.horaSalida }] : [{ ent: '', sal: '' }]);
                              const filas = (base && base.length ? base : [{ ent: '', sal: '' }]);
                              return (
                                <>
                                  {/* títulos */}
                                  <Box style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 8, width: '100%' }}>
                                    <Typography variant="caption">Hora entrada</Typography>
                                    <Typography variant="caption">Hora salida</Typography>
                                  </Box>
                                  {/* filas */}
                                  {filas.map((tr, idx) => (
                                    <Box key={`${s.id}-gentr-${idx}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) 24px', columnGap: 8, alignItems: 'center', width: '100%' }}>
                                      <TextField
                                        type="time"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={tr.ent || ''}
                                        onChange={(e) => setTramoValor(s.id, idx, 'ent', e.target.value)}
                                        inputProps={{ style: { padding: '6px 8px' } }}
                                      />
                                      <TextField
                                        type="time"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={tr.sal || ''}
                                        onChange={(e) => setTramoValor(s.id, idx, 'sal', e.target.value)}
                                        inputProps={{ style: { padding: '6px 8px' } }}
                                      />
                                      <IconButton aria-label="Eliminar corte" size="small" onClick={() => quitarTramo(s.id, idx)} style={{ color: 'var(--garnet)' }}>
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {/* acciones */}
                                  <Box style={{ display: 'grid', gridTemplateColumns: '1fr', columnGap: 8, marginTop: 8, width: '100%' }}>
                                    <Button size="small" fullWidth variant="outlined" style={{ borderColor: 'var(--garnet)', color: 'var(--garnet)', whiteSpace: 'nowrap', textTransform: 'none' }} onClick={() => agregarTramo(s.id)}>Agregar corte</Button>
                                  </Box>
                                </>
                              );
                            })()}
                          </>
                        )}
                      </Box>
                    )}
                    {/* Botón para obtener días disponibles y área expandible de selección dentro de la card */}
                    <Box mt={1}>
                      {(tipoReemplazo === 'VACACIONES') ? (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={async () => {
                            // VACACIONES: el usuario elige QUÉ FECHAS DE COBERTURA (cubre) asignará a este reemplazante.
                            await ensureFechasCupo('COBERTURA');
                            setActiveSegIdForDates(s.id);
                            setOpenFechasModal(true);
                          }}
                        >
                          Elegir fechas para este reemplazante
                        </Button>
                      ) : tipoReemplazo === 'DIRECTO' ? (
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
                          Reemplazo Directo usa sólo la fecha de cobertura seleccionada.
                        </Typography>
                      ) : (
                        <Button
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={async () => {
                            // cargar días disponibles y cupos si corresponde
                            const res = await api.obtenerDiasDisponibles({ fechaInicioRemplazante: fechaInicio, fechaFinRemplazante: fechaFin });
                            if (res.ok) setDiasDisponibles(res.data || []);
                            if (idsSeleccionados.length && fechaInicio && fechaFin) {
                              // cupo por días basado en AUSENCIA (siempre se descuenta del operario ausente)
                              const cupoFechaInicio = (ausenciaInicio || fechaInicio); // usar ausencia como fuente de cupos
                              const cupoFechaFin = (ausenciaFin || fechaFin); // usar ausencia como fuente de cupos
                              const cpd = await api.cupoPorDia({ asignaciones_id: idsSeleccionados, fecha_inicio: cupoFechaInicio, fecha_fin: cupoFechaFin, ps_nombre: puntoSeleccionado });
                              setFechasCupo(cpd.ok ? (cpd.data || []) : []);
                            } else {
                              setFechasCupo([]);
                            }
                            // expandir esta card para mostrar los días inline
                            setExpandedSegIds((prev) => prev.includes(s.id) ? prev : [...prev, s.id]);
                          }}
                        >
                          Obtener días disponibles
                        </Button>
                      )}
                      {/* contenedor expandible con los días disponibles */}
                      {expandedSegIds.includes(s.id) && (
                        <Box mt={1} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                          {(diasDisponibles || []).map((d) => {
                            const active = (s.dias || []).includes(d.nombre);
                            return (
                              <Button
                                key={d.nombre}
                                className="day-chip"
                                variant={active ? 'contained' : 'outlined'}
                                color="primary"
                                onClick={() => handleToggleDiaSeg(s.id, d.nombre)}
                              >
                                {d.nombre}
                              </Button>
                            );
                          })}
                          {/* acción para contraer la sección */}
                          {diasDisponibles.length > 0 && (
                            <Box style={{ width: '100%', textAlign: 'center' }}>
                              <Button size="small" onClick={() => setExpandedSegIds((prev) => prev.filter(id => id !== s.id))}>Ocultar días</Button>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Paper>
                );
              })}
              </div>
              {/* botón agregar debajo del contenedor de tarjetas con scroll */}
              <Box display="flex" justifyContent="center" mt={2}>
                <Button
                  variant="outlined"
                  color="primary"
                   onClick={async () => {
                    if (operarios.length === 0) {
                      const res = await api.listarOperarios();
                      if (res.ok) setOperarios(res.data || []);
                    }
                     setSegmentos((prev) => ([...prev, { id: String(Date.now() + Math.random()), operario: null, horaEntrada: '', horaSalida: '', dias: [], fechas: [] }])); // agregar nueva card
                    window.setTimeout(() => { // esperar render
                      const el = segListRef.current; // referencia del carrusel
                      if (el && typeof (el as any).scrollTo === 'function') {
                        (el as any).scrollTo({ left: el.scrollWidth, behavior: 'smooth' }); // auto-scroll a la última card
                      } else if (el) {
                        el.scrollLeft = el.scrollWidth; // fallback
                      }
                    }, 0);
                  }}
                >
                  Agregar reemplazante
                </Button>
              </Box>
            </Box>

            <Dialog open={openFechasModal} onClose={() => setOpenFechasModal(false)} fullWidth maxWidth="sm">
              <DialogTitle disableTypography>
                <Box>
                  <Typography variant="h6">Seleccionar fechas de cobertura</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Cupo disponible (bolsa): calculado con el rango de ausencia. Estas fechas son las que se van a cubrir.
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent dividers style={{ maxHeight: 420 }}>
                <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
                  {(fechasCupo || [])
                    .filter((f) => (modalDayFilter ? f.dia === modalDayFilter : true))
                    .map((f) => {
                      const selected = activeSegIdForDates
                        ? (segmentos.find((s) => s.id === activeSegIdForDates)?.fechas || []).includes(f.fecha)
                        : fechasSeleccionadas.includes(f.fecha);
                      return (
                        <Button key={f.fecha}
                          variant={selected ? 'contained' : 'outlined'}
                          color="primary"
                          onClick={() => {
                            if (!activeSegIdForDates) return;
                            setSegmentos((prev) => prev.map((s) => {
                              if (s.id !== activeSegIdForDates) return s;
                              const has = (s.fechas || []).includes(f.fecha);
                              const nf = has ? (s.fechas || []).filter((x) => x !== f.fecha) : [ ...(s.fechas || []), f.fecha ];
                              return { ...s, fechas: nf };
                            }));
                          }}
                        >
                          {f.dia.slice(0,3)} {formatFechaCorta(f.fecha)} (máx {f.cupo_total || '00:00'})
                        </Button>
                      );
                    })}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => {
                  if (!activeSegIdForDates) return;
                  setSegmentos((prev) => prev.map((s) => s.id === activeSegIdForDates ? { ...s, fechas: [] } : s));
                }}>Limpiar</Button>
                <Button color="primary" variant="contained" onClick={() => setOpenFechasModal(false)}>Listo</Button>
              </DialogActions>
            </Dialog>

            {errorPaso5 ? (
              <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>{errorPaso5}</Typography>
            ) : null}
          </Box>
        )}

        {/* Sección: guardar (sin resumen previo) */}
        {!showSuccess && (
          <Box mt={4} textAlign="center">
            <Box style={{ maxWidth: 840, margin: '0 auto' }}>
              {/* Bloque de pre-cargas: listado y acciones */}
              {preCargas.length > 0 && (
                <Box mb={2}>
                  <Typography variant="subtitle2" className="mb-2">Pre-cargas ({preCargas.length})</Typography>
                  <div style={{ width: '100%', maxWidth: 840, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {preCargas.map((item, idx) => {
                      const reemplazante = item?.remplazante || '-';
                      const rango = `${item?.fechaInicioRemplazante || ''}${item?.fechaFinRemplazante && item?.fechaFinRemplazante !== item?.fechaInicioRemplazante ? ` a ${item?.fechaFinRemplazante}` : ''}`;
                      const horario = `${item?.horaEntradaRemplazante || '--:--'} - ${item?.horaSalidaRemplazante || '--:--'}`;
                      const ps = item?.puntoServicio || '';
                      return (
                        <Paper key={`pre-${idx}`} elevation={1} style={{ padding: 12 }}>
                          <Box display="flex" style={{ gap: 12 }}>
                            <Box style={{ width: 130, textAlign: 'left' }}>
                              <Typography variant="body2"><strong>Reemplazante</strong></Typography>
                              <Typography variant="body2"><strong>Rango</strong></Typography>
                              <Typography variant="body2"><strong>Horario</strong></Typography>
                              <Typography variant="body2"><strong>Punto de Serv</strong></Typography>
                            </Box>
                            <Box flex={1} style={{ textAlign: 'center', minWidth: 0 }}>
                              <Typography variant="body2" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{reemplazante}</Typography>
                              <Typography variant="body2">{rango || '--'}</Typography>
                              <Typography variant="body2">{horario}</Typography>
                              <Typography variant="body2">{ps || '--'}</Typography>
                            </Box>
                            <Box>
                              <Button
                                size="small"
                                variant="outlined"
                                color="default"
                                style={{ color: 'var(--ion-color-danger)', borderColor: 'var(--ion-color-danger)' }}
                                onClick={() => {
                                  setPreCargas((prev) => prev.filter((_, i) => i !== idx)); // quitar item por índice
                                }}
                              >
                                Quitar
                              </Button>
                            </Box>
                          </Box>
                        </Paper>
                      );
                    })}
                  </div>
                </Box>
              )}

              {/* Acciones de guardado (se elimina “Agregar a pre‑carga”; sólo aparecerá “Guardar todo” si hay ítems) */}
              <Box display="flex" justifyContent="center" style={{ gap: 12, flexWrap: 'wrap' }}>
                {preCargas.length > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={guardarPreCargas}
                    disabled={isSaving}
                  >
                    Guardar todo ({preCargas.length})
                  </Button>
                )}
              </Box>

              <Box mt={2} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    const v = await validarAntesDeContinuar();
                    if (!v.ok) { setErrorPaso5(v.error || 'No se puede guardar.'); return; }
                    await finalizar();
                  }}
                  disabled={isSaving}
                >
                  Guardar reemplazo
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {/* Modal de Confirmación Final */}
        <Dialog open={openConfirmModal} onClose={() => setOpenConfirmModal(false)} fullWidth maxWidth="sm">
          <DialogTitle>Confirmar Reemplazo</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" gutterBottom>Estás a punto de guardar los siguientes datos:</Typography>
            <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={4}>
              <Typography variant="body2"><strong>Tipo:</strong> {tiposReemplazo.find(t => t.value === tipoReemplazo)?.label}</Typography>
              <Typography variant="body2"><strong>Reemplazado:</strong> {operarioRemplazado}</Typography>
              <Typography variant="body2"><strong>Motivo:</strong> {motivo}</Typography>
              <Typography variant="body2"><strong>Punto de Servicio:</strong> {puntoSeleccionado}</Typography>
              <Typography variant="body2"><strong>Rango:</strong> {fechaInicio} al {fechaFin}</Typography>
              {compensaciones.length > 0 && (
                <Typography variant="body2" style={{ color: '#f57c00' }}>
                  <strong>Compensación:</strong> {compensaciones[0].minutos} desde {formatFechaCorta(compensaciones[0].fechaOrigen)}
                </Typography>
              )}
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">Reemplazantes:</Typography>
                {(segmentos || []).map((s, i) => (
                  <Typography key={s.id} variant="body2" style={{ marginLeft: 8 }}>
                    • {s.operario || '(Sin asignar)'} ({s.horaEntrada || '--:--'} - {s.horaSalida || '--:--'})
                  </Typography>
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmModal(false)} disabled={isSaving}>Cancelar</Button>
            <Button onClick={confirmarGuardado} color="primary" variant="contained" disabled={isSaving}>
              {isSaving ? 'Guardando...' : 'Confirmar y Guardar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Loading Overlay Global */}
        <Backdrop open={isSaving} style={{ zIndex: 9999, color: '#fff', flexDirection: 'column' }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6" style={{ marginTop: 16 }}>Guardando...</Typography>
        </Backdrop>
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        
        {/* cierre de contenedor principal */}
        </Box>
      </div>
    </IonContent>
  );
};

export default ReemplazosV2;


